import React, { useState, useEffect, createContext, useContext, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { 
  MessageSquare, 
  User, 
  LogOut, 
  Send, 
  Brain, 
  Phone, 
  Users, 
  Settings,
  Plus,
  Trash2,
  Menu,
  X,
  Shield,
  CheckCircle,
  CreditCard,
  Zap,
  Crown,
  Check,
  Download,
  Mic,
  MicOff,
  Link,
  UserX,
  UserCheck,
  ArrowUp,
  Camera,
  Paperclip,
  Volume2,
  Code,
  Copy,
  Globe,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Car,
  Key,
  HardDrive,
  Sun,
  Moon,
  LayoutGrid,
  Smartphone,
  LayoutTemplate,
  Lightbulb,
  Eye,
  ExternalLink,
  RefreshCw,
  Share2,
  CloudUpload,
  Terminal
} from 'lucide-react';
import './App.css';
import './chronicle-clean-ui.css';
import './chronicle-builder-workspace.css';
import './chronicle-brain-hero.css';
import './chronicle-preview-dock.css';

const CHRONICLE_PREVIEW_DOCK_HIDDEN_KEY = 'chronicle_preview_dock_hidden';
const CHRONICLE_PROJECTS_STORAGE_KEY = 'chronicle_projects_v1';

function chronicleProjectsKey(userId) {
  return `${CHRONICLE_PROJECTS_STORAGE_KEY}_${userId || 'anonymous'}`;
}

function firstNameFromUser(user) {
  const raw = (user?.name || user?.email || 'Builder').trim();
  const part = raw.split(/\s+/)[0] || 'Builder';
  return part.charAt(0).toUpperCase() + part.slice(1);
}

function defaultProjectLabel(user) {
  return `${firstNameFromUser(user)}'s Project`;
}

function newProjectId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `proj-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const CHRONICLE_THEME_KEY = 'chronicle_ui_theme';
const CHRONICLE_BUILDER_TAB_KEY = 'chronicle_builder_product_tab';
const CHRONICLE_SIDEBAR_COLLAPSED_KEY = 'chronicle_sidebar_collapsed';

const BUILDER_PRODUCT_TABS = [
  { id: 'fullstack', label: 'Full stack', placeholder: 'Build me a full stack app that…' },
  { id: 'mobile', label: 'Mobile', placeholder: 'Build me a mobile website that…' },
  { id: 'landing', label: 'Landing page', placeholder: 'Build me a landing page that…' },
  { id: 'brainstorm', label: 'Brainstorm', placeholder: 'Help me brainstorm ideas for…' },
];

// Same host as the page, port 8000 — so opening http://MACHINE:3000 still hits your API on http://MACHINE:8000 (not 127.0.0.1 on the client).
// Set REACT_APP_BACKEND_URL only when you need a different API (e.g. production split origin).
const stripAnsi = (str) => String(str || '').replace(/\x1B\[[0-9;]*m|\[[0-9;]*m/g, '').trim();

const PROVIDER_DISPLAY_NAMES = {
  anthropic: 'Claude',
  openai: 'OpenAI',
  google: 'Google Gemini',
  gemini: 'Google Gemini',
  groq: 'Groq',
  xai: 'xAI',
  deepseek: 'DeepSeek',
  mistral: 'Mistral',
};

function providerDisplayName(provider) {
  if (!provider) return null;
  const key = String(provider).toLowerCase().trim();
  return PROVIDER_DISPLAY_NAMES[key] || String(provider);
}

function activeBrainLabel({ provider, model, appBuilderMode, builderTier }) {
  const name = providerDisplayName(provider);
  if (name) return model ? `${name} (${model})` : name;
  if (appBuilderMode && builderTier) return `Builder · ${builderTier}`;
  return 'your active brain';
}

/** Credit / billing toasts — names the brain provider (Claude, OpenAI, etc.). */
function formatCreditToastMessage(kind, creditStatus, brainCtx = {}) {
  const brain = activeBrainLabel(brainCtx);
  const creditsLeft = Number(creditStatus?.credits);
  const balance =
    Number.isFinite(creditsLeft) ? ` (${creditsLeft} Chronicle credit${creditsLeft === 1 ? '' : 's'} left)` : '';
  if (kind === 'exhausted') {
    return `Chronicle credits exhausted while using ${brain}${balance}. Open Menu → Subscription to top up.`;
  }
  if (kind === 'low') {
    return `Chronicle credits running low on ${brain}${balance}. Open Menu → Subscription to top up.`;
  }
  const base = (creditStatus?.warning_message || '').trim();
  if (base && brainCtx.provider) {
    return `${base} Active brain: ${activeBrainLabel(brainCtx)}.`;
  }
  return base || formatCreditToastMessage('low', creditStatus, brainCtx);
}

/** FastAPI / axios error body → toast text (provider-specific messages from backend). */
function apiErrorDetail(err, brainCtx = {}) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    if (d.code === 'INSUFFICIENT_CREDITS') {
      const brain = activeBrainLabel(brainCtx);
      const bal = Number.isFinite(Number(d.credits)) ? ` (${Number(d.credits)} left)` : '';
      const feat = d.feature ? ` — ${d.feature}` : '';
      return `Not enough Chronicle credits for ${brain}${feat}${bal}. Menu → Subscription.`;
    }
    if (typeof d.warning_message === 'string' && d.warning_message.trim()) {
      const brain = brainCtx.provider ? ` (${activeBrainLabel(brainCtx)})` : '';
      return `${d.warning_message.trim()}${brain}`;
    }
    if (typeof d.message === 'string' && d.message.trim()) return d.message.trim();
  }
  if (Array.isArray(d) && d.length) {
    return d
      .map((x) => (typeof x === 'string' ? x : x?.msg || ''))
      .filter(Boolean)
      .join(' — ');
  }
  const msg = err?.response?.data?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  return null;
}

const API_URL = (() => {
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  if (fromEnv && String(fromEnv).trim() !== '' && fromEnv !== 'undefined') {
    return String(fromEnv).replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }
  return 'http://127.0.0.1:8000';
})();

/** True when the bundled API base is clearly your machine / Docker dev (not production). */
const apiPointsAtLocalBackend = (() => {
  try {
    const u = new URL(API_URL);
    const h = u.hostname.toLowerCase();
    if (h === 'localhost' || h === '127.0.0.1') return true;
    const port = String(u.port || '');
    if (port === '8000') return true;
    if (/^(10\.|192\.168\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(h)) return true;
    return false;
  } catch {
    return false;
  }
})();

const API = API_URL;
const CHRONICLE_OWNER_SWITCHBOARD_BRAND = 'Chronicle';

/** Chronicle → Vaulted Switchboard (magic login, new tab). Owner uses admin mint. */
async function openChronicleSwitchboard(isOwnerAdmin = false) {
  try {
    const token = readStoredToken();
    if (!token) {
      toast.error('Sign in first');
      return;
    }
    const path = isOwnerAdmin ? '/api/admin/switchboard-enter' : '/api/switchboard-enter';
    const res = await axios.post(
      `${API_URL}${path}`,
      { brand: CHRONICLE_OWNER_SWITCHBOARD_BRAND },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const url = res.data?.url;
    const label = res.data?.label || 'Vaulted Switchboard';
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(`Opening ${label}`);
    } else {
      toast.error('No Switchboard URL returned');
    }
  } catch (err) {
    toast.error(
      err.response?.data?.detail || err.message || 'Could not open Switchboard'
    );
  }
}

/** Dev-login and /me must not hang forever when Docker/backend is down (browser default = no timeout). */
const API_REQUEST_TIMEOUT_MS = 15000;
const GOOGLE_OAUTH_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const IS_LOCAL_DEV =
  typeof window !== 'undefined' &&
  (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost');

/** Billing/trial skip + dev-login: localhost, compose flag, or API URL is local (fixes missing .env.local). */
const SKIP_BILLING_GATE =
  IS_LOCAL_DEV ||
  process.env.REACT_APP_SKIP_BILLING_GATE === '1' ||
  process.env.REACT_APP_SKIP_BILLING_GATE === 'true' ||
  apiPointsAtLocalBackend;

/** With SKIP_BILLING_GATE: skip dev auto-login and use /auth (register + login) like production. */
const NORMAL_LOGIN_ONLY =
  process.env.REACT_APP_NORMAL_LOGIN_ONLY === '1' ||
  process.env.REACT_APP_NORMAL_LOGIN_ONLY === 'true';

/** Session id on first paint — Bridge toggle and send() must not wait on async /chat/sessions. */
function createInitialChatSessionId() {
  if (typeof window !== 'undefined' && typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Keep JWT + dev session in sessionStorage on typical dev hosts so two tabs aren't one user.
 * Uses hostname (not only SKIP_BILLING_GATE) so a mis-built bundle on localhost still isolates tabs.
 */
function tabScopedAuthStorageEnabled() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1') return true;
  if (/^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(h)) return true;
  return SKIP_BILLING_GATE;
}

/** Which local dev session is active — per-tab in dev (`owner` = admin dev-login-owner). */
const CHRONICLE_DEV_SESSION_KEY = 'chronicleDevSession';

/** Remember `?admin=1` across navigates (Router strips query before Dashboard reads it). */
const CHRONICLE_DEEP_LINK_ADMIN_KEY = 'chronicleDeepLinkAdmin';

/** One-time per browser profile: drop legacy shared localStorage JWT (not per-tab session keys). */
const CHRONICLE_LEGACY_AUTH_MIGRATE_KEY = 'chronicleAuthDidMigrateV2';

function normalizeTokenRaw(raw) {
  const t = String(raw || '').trim().replace(/^"+|"+$/g, '');
  if (!t || t === 'undefined' || t === 'null') return null;
  return t;
}

/** Tab-scoped dev hosts: sessionStorage; otherwise localStorage (production). */
function readStoredToken() {
  if (typeof window === 'undefined') return null;
  if (tabScopedAuthStorageEnabled()) {
    try {
      if (localStorage.getItem(CHRONICLE_LEGACY_AUTH_MIGRATE_KEY) !== '1') {
        localStorage.removeItem('token');
        localStorage.setItem(CHRONICLE_LEGACY_AUTH_MIGRATE_KEY, '1');
      }
    } catch {
      /* ignore quota / private mode */
    }
    let raw = sessionStorage.getItem('token');
    if (!raw) raw = localStorage.getItem('token');
    return normalizeTokenRaw(raw);
  }
  return normalizeTokenRaw(localStorage.getItem('token'));
}

function clearStoredToken() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('token');
  localStorage.removeItem('token');
}

function writeStoredToken(at) {
  if (typeof window === 'undefined') return;
  const t = String(at || '').trim();
  if (!t) {
    clearStoredToken();
    return;
  }
  if (tabScopedAuthStorageEnabled()) {
    sessionStorage.setItem('token', t);
    localStorage.removeItem('token');
  } else {
    localStorage.setItem('token', t);
  }
}

function readDevSessionKind() {
  if (typeof window === 'undefined') return null;
  if (tabScopedAuthStorageEnabled()) {
    return sessionStorage.getItem(CHRONICLE_DEV_SESSION_KEY) || localStorage.getItem(CHRONICLE_DEV_SESSION_KEY);
  }
  return localStorage.getItem(CHRONICLE_DEV_SESSION_KEY);
}

function writeDevSessionKind(v) {
  if (typeof window === 'undefined') return;
  if (tabScopedAuthStorageEnabled()) {
    sessionStorage.setItem(CHRONICLE_DEV_SESSION_KEY, v);
    localStorage.removeItem(CHRONICLE_DEV_SESSION_KEY);
  } else {
    localStorage.setItem(CHRONICLE_DEV_SESSION_KEY, v);
  }
}

/**
 * Local docker dev: default to owner admin (Bridge toggle, Admin nav) when kind is unset.
 * Use ?devRegular=1 once to persist "regular" dev user; ?devOwner=1 for owner.
 */
function shouldUseDevLoginOwner(params) {
  const p = params instanceof URLSearchParams ? params : new URLSearchParams(params || '');
  if (p.get('devOwner') === '1') return true;
  if (p.get('devRegular') === '1') return false;
  const kind = readDevSessionKind();
  if (kind === 'regular') return false;
  if (kind === 'owner') return true;
  return !!(SKIP_BILLING_GATE && !NORMAL_LOGIN_ONLY);
}

function clearDevSessionKind() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(CHRONICLE_DEV_SESSION_KEY);
  localStorage.removeItem(CHRONICLE_DEV_SESSION_KEY);
}

function captureAdminDeepLinkIntentFromSearch(searchStr) {
  try {
    const q = searchStr || '';
    const p = new URLSearchParams(q.startsWith('?') ? q.slice(1) : q);
    if (p.get('admin') === '1') {
      sessionStorage.setItem(CHRONICLE_DEEP_LINK_ADMIN_KEY, '1');
    }
  } catch {
    /* ignore */
  }
}

/** Browser speech-to-text (Chrome / Edge / some Safari) — fallback when Whisper fails (e.g. missing OPENAI_API_KEY). */
function transcribeWithBrowserSpeechOnce() {
  const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SR) return Promise.reject(new Error('Browser speech not supported'));
  return new Promise((resolve, reject) => {
    const rec = new SR();
    rec.lang = 'en-GB';
    rec.interimResults = false;
    rec.continuous = false;
    let settled = false;
    const fail = (msg) => {
      if (settled) return;
      settled = true;
      reject(new Error(msg));
    };
    const timer = window.setTimeout(() => fail('Speech timed out — try again'), 35000);
    rec.onresult = (e) => {
      window.clearTimeout(timer);
      const text = e.results[0]?.[0]?.transcript?.trim() || '';
      if (!settled) {
        settled = true;
        resolve(text);
      }
    };
    rec.onerror = (ev) => {
      window.clearTimeout(timer);
      fail(ev.error || 'Speech recognition error');
    };
    rec.onend = () => {
      window.clearTimeout(timer);
      if (!settled) fail('No speech detected');
    };
    try {
      rec.start();
    } catch (err) {
      window.clearTimeout(timer);
      fail(err.message || 'Could not start speech recognition');
    }
  });
}

// Voice Input Component — ref exposes startListening() for Car Mode hands-free loop
const VoiceInput = forwardRef(function VoiceInput(
  { onTranscription, disabled, onRecordingChange, speechRecognitionFallback, suppressFallbackInfoToast, deviceOnly = false },
  ref
) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const streamRef = useRef(null);

  const stopRecording = useCallback(() => {
    setAudioLevel(0);
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (onRecordingChange) onRecordingChange(false);
  }, [onRecordingChange]);

  const startRecording = async () => {
    if (isRecording || isProcessing) return;
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Find supported format
      let mimeType = '';
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      chunksRef.current = [];

      // Audio analysis
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      let lastSoundTime = Date.now();

      silenceTimerRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        
        setAudioLevel(Math.min(100, average * 2));
        
        if (average > 8) {
          lastSoundTime = Date.now();
        } else if (Date.now() - lastSoundTime > 2000) {
          stopRecording();
        }
      }, 50);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        if (silenceTimerRef.current) clearInterval(silenceTimerRef.current);
        if (audioContextRef.current) audioContextRef.current.close();
        
        setIsProcessing(true);
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        if (!blob || blob.size < 32) {
          toast.error('Recording too short — hold the mic and speak, then wait for silence.');
          setIsProcessing(false);
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
          }
          return;
        }
        const formData = new FormData();
        formData.append('audio', blob, 'recording.webm');

        try {
          const token = readStoredToken();
          const response = await axios.post(`${API}/api/voice/transcribe`, formData, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.data.text) onTranscription(response.data.text);
        } catch (err) {
          const raw = err.response?.data?.detail;
          let serverMsg;
          if (typeof raw === 'string') serverMsg = raw;
          else if (Array.isArray(raw)) {
            serverMsg = raw.map((x) => (typeof x === 'string' ? x : x?.msg || JSON.stringify(x))).join('; ');
          } else if (raw && typeof raw === 'object') serverMsg = raw.msg || JSON.stringify(raw);
          else serverMsg = err.response?.data?.message;
          if (!serverMsg && !err.response) {
            serverMsg = err.message?.includes('Network Error')
              ? `Cannot reach API at ${API} — is the backend running?`
              : err.message || 'Request failed';
          }
          const fallbackOk =
            speechRecognitionFallback &&
            (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition));
          if (fallbackOk) {
            if (!suppressFallbackInfoToast) {
              toast.info('Whisper failed — using device speech. Speak now…');
            }
            try {
              const text = await transcribeWithBrowserSpeechOnce();
              if (text) onTranscription(text);
              else toast.error('No words recognized');
            } catch (e2) {
              toast.error(serverMsg || e2.message || 'Failed to transcribe');
            }
          } else {
            toast.error(serverMsg || 'Failed to transcribe');
          }
        } finally {
          setIsProcessing(false);
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      if (onRecordingChange) onRecordingChange(true);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  // Device-only recognition (no backend, Web Speech API once)
  const startDeviceRecognition = async () => {
    if (isProcessing) return;
    try {
      onRecordingChange?.(true);
      setIsProcessing(true);
      const text = await transcribeWithBrowserSpeechOnce();
      if (text) onTranscription(text);
      else toast.error('No words recognized');
    } catch (e) {
      toast.error(e.message || 'Failed to transcribe');
    } finally {
      setIsProcessing(false);
      onRecordingChange?.(false);
    }
  };

  const handleClick = () => {
    if (isProcessing) return;
    if (deviceOnly) {
      // One-tap device speech (no record/stop toggling)
      startDeviceRecognition();
      return;
    }
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  useImperativeHandle(ref, () => ({
    startListening: () => (deviceOnly ? startDeviceRecognition() : startRecording()),
  }));

  const glowStyle = isRecording ? {
    boxShadow: `0 0 ${10 + audioLevel / 4}px ${5 + audioLevel / 8}px rgba(239, 68, 68, ${0.4 + audioLevel / 150})`
  } : {};

  return (
    <button
      type="button"
      className={`voice-input-btn ${isRecording ? 'recording' : ''} ${isProcessing ? 'processing' : ''}`}
      onClick={handleClick}
      disabled={disabled || isProcessing}
      style={glowStyle}
      data-testid="voice-input-btn"
    >
      {isProcessing ? <div className="voice-spinner" /> : <Mic size={20} />}
    </button>
  );
});

// Install Button Component - only shows in browser, not when installed as app
const InstallButton = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setIsInstalled(true);
    }
    setInstallPrompt(null);
  };

  if (isInstalled) return null;

  return (
    <button 
      className="install-app-btn"
      onClick={handleInstall}
      data-testid="install-app-btn"
    >
      <Download size={18} />
      Install Chronicle App
    </button>
  );
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => readStoredToken());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  /** Router `location.search` can lag the real URL on first paint — auth must read `window.location`. */
  const getBrowserSearch = () =>
    typeof window !== 'undefined' ? window.location.search || '' : '';

  const tryDevLogin = async () => {
    // Never hit dev-login on production — server returns 405 and breaks 401 recovery.
    if (!SKIP_BILLING_GATE || NORMAL_LOGIN_ONLY) return false;
    try {
      const params = new URLSearchParams(getBrowserSearch());
      if (typeof window !== 'undefined') {
        if (params.get('devOwner') === '1') {
          writeDevSessionKind('owner');
        }
        if (params.get('devRegular') === '1') {
          writeDevSessionKind('regular');
        }
      }
      const devOwner = shouldUseDevLoginOwner(params);
      captureAdminDeepLinkIntentFromSearch(getBrowserSearch());
      const devPath = devOwner ? '/api/auth/dev-login-owner' : '/api/auth/dev-login';
      const devRes = await axios({
        method: 'post',
        url: `${API_URL}${devPath}`,
        timeout: API_REQUEST_TIMEOUT_MS,
      });
      const at = String(devRes.data.access_token || '').trim();
      writeStoredToken(at);
      setToken(at);
      axios.defaults.headers.common['Authorization'] = `Bearer ${at}`;
      setUser(devRes.data.user);
      if (typeof window !== 'undefined') {
        writeDevSessionKind(devOwner ? 'owner' : 'regular');
        const u = new URL(window.location.href);
        u.searchParams.delete('devOwner');
        u.searchParams.delete('devRegular');
        const nextPath = `${u.pathname}${u.search}${u.hash}`;
        navigate(nextPath, { replace: true });
      }
      return true;
    } catch (e2) {
      return false;
    }
  };

  const tryDevLoginRef = useRef(tryDevLogin);
  tryDevLoginRef.current = tryDevLogin;

  useEffect(() => {
    const qs = getBrowserSearch() || location.search || '';
    const bootParams = new URLSearchParams(qs);
    if (bootParams.get('devOwner') === '1' || bootParams.get('devRegular') === '1') {
      return;
    }
    const normalized = readStoredToken();
    if (normalized) {
      writeStoredToken(normalized);
    } else {
      clearStoredToken();
    }
  }, [location.search]);

  useEffect(() => {
    const normalizeDetail = (data) => {
      const d = data?.detail;
      if (!d) return '';
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) {
        return d
          .map((x) => (x && typeof x.msg === 'string' ? x.msg : JSON.stringify(x)))
          .join(' ');
      }
      return String(d);
    };

    // Always send the latest stored token (fixes stale Bearer after login / refresh).
    const reqId = axios.interceptors.request.use((config) => {
      const url = String(config?.url || '');
      if (!url.includes('/api/')) return config;
      if (
        url.includes('/api/auth/login') ||
        url.includes('/api/auth/register') ||
        url.includes('/api/auth/forgot-password') ||
        url.includes('/api/auth/google/token') ||
        url.includes('/api/auth/dev-login') ||
        url.includes('/api/auth/dev-login-owner')
      ) {
        return config;
      }
      const t = readStoredToken();
      if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });

    const respId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const detail = normalizeDetail(error?.response?.data).toLowerCase();
        const original = error?.config || {};
        const url = String(original.url || '');
        const authExempt =
          !url.includes('/api/') ||
          url.includes('/api/auth/dev-login') ||
          url.includes('/api/auth/dev-login-owner') ||
          url.includes('/api/auth/login') ||
          url.includes('/api/auth/register') ||
          url.includes('/api/auth/forgot-password') ||
          url.includes('/api/auth/google/token');
        const canRecover = status === 401 && !original._retry && !authExempt;
        /** Local dev: any 401 on /api/* (stale JWT, DB reset, SECRET_KEY change) → fresh dev-login. */
        if (SKIP_BILLING_GATE && NORMAL_LOGIN_ONLY && canRecover) {
          clearStoredToken();
          delete axios.defaults.headers.common['Authorization'];
          window.location.assign(`${window.location.origin}/auth`);
          return Promise.reject(error);
        }
        const shouldDevRecover = SKIP_BILLING_GATE && canRecover && !NORMAL_LOGIN_ONLY;
        const shouldProdRecover =
          !SKIP_BILLING_GATE && canRecover && (detail.includes('invalid token') || detail.includes('session expired'));
        if (shouldDevRecover || shouldProdRecover) {
          if (SKIP_BILLING_GATE && !NORMAL_LOGIN_ONLY) {
            original._retry = true;
            const recovered = await tryDevLoginRef.current();
            if (recovered) {
              const newToken = readStoredToken();
              if (newToken) {
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newToken}`;
                return axios(original);
              }
            }
          } else {
            // Production: never call dev-login — stale JWT after DB rebuild etc.
            clearStoredToken();
            delete axios.defaults.headers.common['Authorization'];
            window.location.assign(`${window.location.origin}/auth`);
            return Promise.reject(error);
          }
          // Local dev: never send user to /auth — clear token; UI retries dev-login (ProtectedRoute gate).
          clearStoredToken();
          delete axios.defaults.headers.common['Authorization'];
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(respId);
    };
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`, { timeout: API_REQUEST_TIMEOUT_MS });
      setUser(res.data);
    } catch (err) {
      const recovered = await tryDevLogin();
      if (!recovered) {
        if (SKIP_BILLING_GATE) {
          clearStoredToken();
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];
          // Do not set a fake "logged-in" user without a JWT — API calls will all 401.
          setUser(null);
        } else {
          logout();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If returning from OAuth callback with code param, skip the /me check.
    // AuthCallback will exchange the code and establish the session first.
    const urlParams = new URLSearchParams(getBrowserSearch() || location.search || '');
    if (urlParams.get('code')) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      captureAdminDeepLinkIntentFromSearch(getBrowserSearch() || location.search || '');
      const urlParams = new URLSearchParams(getBrowserSearch() || location.search || '');
      if (urlParams.get('devOwner') === '1') {
        writeDevSessionKind('owner');
        clearStoredToken();
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
      }
      if (urlParams.get('devRegular') === '1') {
        writeDevSessionKind('regular');
        clearStoredToken();
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
      }
      const effectiveToken = readStoredToken();
      if (effectiveToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${effectiveToken}`;
        await fetchUser();
      } else if (SKIP_BILLING_GATE && !NORMAL_LOGIN_ONLY) {
        let recovered = false;
        for (let i = 0; i < 5 && !cancelled && !recovered; i++) {
          recovered = await tryDevLogin();
          if (recovered) break;
          await new Promise((r) => setTimeout(r, 2000));
        }
        if (!recovered) {
          setUser(null);
          toast.error(
            'Cannot reach Chronicle API (dev login). Ensure the backend is running on port 8000 (native or Docker).',
            { duration: 10000 },
          );
        }
      } else {
        const recovered = await tryDevLogin();
        if (!recovered) setUser(null);
      }
      if (!cancelled) {
        setLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const at = String(res.data.access_token || '').trim();
    writeStoredToken(at);
    axios.defaults.headers.common['Authorization'] = `Bearer ${at}`;
    setToken(at);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    const at = String(res.data.access_token || '').trim();
    writeStoredToken(at);
    axios.defaults.headers.common['Authorization'] = `Bearer ${at}`;
    setToken(at);
    setUser(res.data.user);
    return res.data;
  };

  const loginWithGoogle = async (code, redirectUri) => {
    const res = await axios.post(`${API_URL}/api/auth/google/token`, { 
      code: code,
      redirect_uri: redirectUri 
    });
    const at = String(res.data.session_token || '').trim();
    writeStoredToken(at);
    axios.defaults.headers.common['Authorization'] = `Bearer ${at}`;
    setToken(at);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    clearStoredToken();
    clearDevSessionKind();
    try {
      sessionStorage.removeItem(CHRONICLE_DEEP_LINK_ADMIN_KEY);
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, loginWithGoogle, logout, loading, updateUser, tryDevLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login/Register Page
const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const { login, register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!SKIP_BILLING_GATE || NORMAL_LOGIN_ONLY) return;
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (authLoading || !user) return;
    navigate('/', { replace: true });
  }, [authLoading, user, navigate]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetSending(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: resetEmail });
      toast.success('Password reset link sent to your email');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setResetSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      toast.success(isLogin ? 'Welcome back!' : 'Account created!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    if (IS_LOCAL_DEV) {
      toast.error('Google sign-in is disabled in local dev.');
      return;
    }
    if (!GOOGLE_OAUTH_CLIENT_ID) {
      toast.error('Google sign-in is not configured (set REACT_APP_GOOGLE_CLIENT_ID).');
      return;
    }
    const clientId = GOOGLE_OAUTH_CLIENT_ID;
    const redirectUri = window.location.origin + '/auth/google/callback';
    const scope = 'email profile';
    
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scope)}` +
      `&access_type=offline` +
      `&prompt=consent`;
    
    window.location.href = googleAuthUrl;
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Brain className="auth-logo" />
          <h1>Chronicle</h1>
          <p>The AI that never forgets</p>
        </div>

        {!IS_LOCAL_DEV && (
          <button 
            onClick={handleGoogleLogin}
            className="google-btn"
            type="button"
            data-testid="google-login-btn"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.26c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
              <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
        )}

        <p className="trial-text" data-testid="trial-text">
          <Zap size={14} /> 10-day free trial
        </p>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required={!isLogin}
                data-testid="register-name-input"
              />
            </div>
          )}
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              data-testid="auth-email-input"
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
              data-testid="auth-password-input"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={submitting}
            data-testid="auth-submit-btn"
          >
            {submitting ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        {isLogin && (
          <button 
            className="forgot-password-btn"
            onClick={() => setShowForgotPassword(true)}
            data-testid="forgot-password-btn"
          >
            Forgot your password?
          </button>
        )}

        <p className="auth-switch">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button 
            onClick={() => setIsLogin(!isLogin)}
            data-testid="auth-switch-btn"
          >
            {isLogin ? 'Sign up' : 'Sign in'}
          </button>
        </p>

        <div className="auth-footer-links">
          <a href="/privacy">Privacy Policy</a>
          <span>•</span>
          <a href="/terms">Terms of Service</a>
        </div>

        <InstallButton />
      </div>

      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Reset Password</h2>
            <p>Enter your email and we'll send you a reset link</p>
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  data-testid="reset-email-input"
                />
              </div>
              <button 
                type="submit" 
                className="auth-button"
                disabled={resetSending}
              >
                {resetSending ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <button 
              className="modal-close"
              onClick={() => setShowForgotPassword(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Google OAuth Callback
const AuthCallback = () => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Do not navigate to "/" on localhost without a token (causes / → /auth bounce).
      // Get authorization code from URL query params (Google's response)
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      
      if (error) {
        toast.error('Google sign-in was cancelled');
        navigate(SKIP_BILLING_GATE ? '/' : '/auth');
        return;
      }
      
      if (!code) {
        toast.error('Authentication failed - no authorization code');
        navigate(SKIP_BILLING_GATE ? '/' : '/auth');
        return;
      }

      const redirectUri = window.location.origin + '/auth/google/callback';

      try {
        const result = await loginWithGoogle(code, redirectUri);
        toast.success('Welcome!');

        if (SKIP_BILLING_GATE || result.user.disclosure_accepted) {
          navigate('/');
        } else {
          navigate('/disclosure');
        }
      } catch (err) {
        console.error('Google auth error:', err);
        toast.error(err.response?.data?.detail || 'Google sign-in failed');
        navigate(SKIP_BILLING_GATE ? '/' : '/auth');
      }
    };

    processCallback();
  }, [loginWithGoogle, navigate, searchParams]);

  return (
    <div className="loading-screen">
      <Brain size={48} className="spin" />
      <p>Signing you in...</p>
    </div>
  );
};

// Disclosure Page
const DisclosurePage = () => {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (SKIP_BILLING_GATE) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleAccept = async () => {
    if (!accepted) {
      toast.error('Please check the box to accept the terms');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/disclosure/accept`, { accepted: true });
      updateUser({ disclosure_accepted: true });
      toast.success('Terms accepted!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept terms');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="disclosure-page">
      <div className="disclosure-container">
        <div className="disclosure-header">
          <Shield className="disclosure-icon" />
          <h1>Terms & Disclosure</h1>
          <p>Please read and accept before using Chronicle</p>
        </div>

        <div className="disclosure-content">
          <h2>Data Storage & Privacy</h2>
          <ul>
            <li>Chronicle stores your conversations and personal information to provide persistent memory features.</li>
            <li>Your data is stored securely and is only accessible by you.</li>
            <li>We do not sell or share your personal information with third parties.</li>
            <li>You can delete your data at any time from the settings page.</li>
          </ul>

          <h2>AI Limitations</h2>
          <ul>
            <li>Chronicle is an artificial intelligence and may make mistakes.</li>
            <li>Always verify important information independently.</li>
            <li>Do not rely on Chronicle for medical, legal, or financial advice.</li>
          </ul>

          <h2>Phone & Messaging Features</h2>
          <ul>
            <li>If you use phone/SMS features, standard carrier rates may apply.</li>
            <li>You are responsible for ensuring you have consent to contact others.</li>
            <li>Chronicle will send messages and make calls on your behalf as instructed.</li>
          </ul>

          <h2>Subscription & Billing</h2>
          <ul>
            <li>Some features require a paid subscription.</li>
            <li>You can cancel your subscription at any time.</li>
            <li>Refunds are handled according to our refund policy.</li>
          </ul>
        </div>

        <div className="disclosure-accept">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="disclosure-checkbox"
            />
            <span>I have read and agree to the terms above</span>
          </label>

          <button
            onClick={handleAccept}
            disabled={!accepted || submitting}
            className="accept-button"
            data-testid="disclosure-accept-btn"
          >
            {submitting ? 'Processing...' : 'Accept & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Trial Setup Page - Collect card details before using the app
const TrialSetupPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (SKIP_BILLING_GATE) {
      navigate('/', { replace: true });
      return;
    }
    // If user already has card on file or is subscribed, redirect to dashboard
    if (user?.card_on_file || user?.is_subscribed) {
      navigate('/');
      return;
    }
    fetchPlans();
  }, [user, navigate]);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/plans`);
      // Only show monthly plans for trial setup
      const monthlyPlans = res.data.plans.filter(p => p.interval === 'month');
      setPlans(monthlyPlans);
    } catch (err) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async (planId) => {
    if (SKIP_BILLING_GATE) {
      toast.message('Local dev: billing is off.');
      navigate('/', { replace: true });
      return;
    }
    setProcessingPlan(planId);
    try {
      const res = await axios.post(`${API_URL}/api/trial/setup`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe to collect card
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start trial setup');
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    if (planId.includes('business')) return <Crown size={24} />;
    if (planId.includes('pro')) return <Zap size={24} />;
    return <Brain size={24} />;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <Brain size={48} className="spin" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="trial-setup-page">
      <div className="trial-setup-container">
        <div className="trial-setup-header">
          <Brain size={48} />
          <h1>Start Your 10-Day Free Trial</h1>
          <p>Choose a plan - you won't be charged until day 11</p>
          <p className="trial-note">Card required to start trial. Cancel anytime.</p>
          <div className="trial-features-note">
            <p><Check size={14} /> Full AI assistant with memory</p>
            <p><Check size={14} /> Phone & SMS unlocks after trial</p>
          </div>
        </div>

        <div className="trial-plans-grid">
          {plans.map(plan => (
            <div key={plan.id} className={`trial-plan-card ${plan.id.includes('pro') ? 'featured' : ''}`}>
              {plan.id.includes('pro') && <div className="popular-badge">Most Popular</div>}
              <div className="plan-icon">{getPlanIcon(plan.id)}</div>
              <h3>Chronicle {plan.name}</h3>
              <div className="plan-price">
                <span className="amount">£{plan.price}</span>
                <span className="period">/month</span>
              </div>
              <p className="after-trial">Billed monthly after 10-day trial</p>
              <ul className="plan-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}><Check size={16} /> {feature}</li>
                ))}
              </ul>
              <button
                onClick={() => handleStartTrial(plan.id)}
                disabled={processingPlan !== null}
                className="trial-start-btn"
                data-testid={`trial-start-${plan.id}`}
              >
                {processingPlan === plan.id ? 'Processing...' : 'Start Free Trial'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Trial Success Page
const TrialSuccess = () => {
  const [checking, setChecking] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  useEffect(() => {
    if (SKIP_BILLING_GATE) {
      navigate('/', { replace: true });
      return;
    }
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkTrialStatus(sessionId);
    } else {
      navigate('/trial-setup');
    }
  }, [searchParams, navigate]);

  const checkTrialStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/trial/status/${sessionId}`);
      
      if (res.data.trial_active) {
        updateUser({ 
          card_on_file: true, 
          is_subscribed: true,
          trial_active: false 
        });
        toast.success('Subscription activated! Welcome to Chronicle.');
        setTimeout(() => navigate('/'), 2000);
      } else {
        // Still processing
        setTimeout(() => checkTrialStatus(sessionId), 2000);
      }
    } catch (err) {
      toast.error('Failed to verify subscription status');
      navigate('/trial-setup');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="success-page">
      <div className="success-container">
        <CheckCircle size={64} className="success-icon" />
        <h1>Activating Your Subscription...</h1>
        <p>Please wait while we set up your account</p>
        {checking && <Brain size={32} className="spin" />}
      </div>
    </div>
  );
};

/** PC Bridge OFF/ON — shared by chat toolbar and sidebar (Admin opens without Chat mounted). Icon-only in UI; details in tooltip. */
function BridgeUnlockControls({ sessionId, wrapperClassName }) {
  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);
  const [bridgeUiEligible, setBridgeUiEligible] = useState(false);
  const [bridgeUnlocked, setBridgeUnlocked] = useState(false);
  /** Node Bridge on your PC (port 3001) — not the same as "Bridge ON" for this chat. */
  const [hostBridge, setHostBridge] = useState({ ok: null, error: '' });
  /** Sidebar / race: parent session id can be null briefly — never hide Bridge for lack of id in local dev. */
  const [fallbackSessionId] = useState(() => createInitialChatSessionId());
  const { user, token } = useAuth();
  /** Owner / granted users need a session id for PUT bridge-unlock even when Chat isn't mounted (e.g. Admin view only). */
  const mayUseBridgeSessionFallback =
    SKIP_BILLING_GATE || isOwnerAdmin || bridgeUiEligible;
  const effectiveSessionId =
    sessionId || (mayUseBridgeSessionFallback ? fallbackSessionId : null);
  const showBridgeToggle = !!effectiveSessionId && mayUseBridgeSessionFallback;

  const loadHostBridgeStatus = useCallback(async () => {
    if (!showBridgeToggle) return null;
    try {
      const t = readStoredToken();
      if (!t) {
        setHostBridge({ ok: null, error: '' });
        return null;
      }
      const res = await axios.get(`${API_URL}/api/bridge/host-status`, {
        headers: { Authorization: `Bearer ${t}` },
        timeout: 12000,
      });
      const ok = res.data?.ok === true;
      setHostBridge({
        ok,
        error: (res.data?.error && String(res.data.error)) || '',
      });
      return ok;
    } catch (err) {
      const d = err.response?.data?.detail;
      setHostBridge({
        ok: false,
        error: typeof d === 'string' ? d : err.message || 'unreachable',
      });
      return false;
    }
  }, [showBridgeToggle]);

  const fetchBridgeUnlockForSession = useCallback(async (sid) => {
    if (!sid) {
      setBridgeUnlocked(false);
      return;
    }
    try {
      const t = readStoredToken();
      if (!t) return;
      const res = await axios.get(
        `${API_URL}/api/chat/sessions/bridge-unlock?session_id=${encodeURIComponent(sid)}`,
        { headers: { Authorization: `Bearer ${t}` } }
      );
      if (res.data?.visible) {
        setBridgeUnlocked(!!res.data.bridge_unlocked);
      } else {
        setBridgeUnlocked(false);
      }
    } catch {
      setBridgeUnlocked(false);
    }
  }, []);

  const loadBridgeAdminFlags = useCallback(async () => {
    try {
      const t = readStoredToken();
      if (!t) {
        setIsOwnerAdmin(false);
        setBridgeUiEligible(false);
        return;
      }
      const res = await axios.get(`${API_URL}/api/user/is-admin`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setIsOwnerAdmin(!!res.data?.is_admin);
      setBridgeUiEligible(!!res.data?.bridge_ui_eligible);
    } catch {
      setIsOwnerAdmin(false);
      setBridgeUiEligible(false);
    }
  }, []);

  useEffect(() => {
    loadBridgeAdminFlags();
  }, [user?.id, token, loadBridgeAdminFlags]);

  /** Grant can land in Mongo while this tab still has stale flags — refetch when tab wakes up. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadBridgeAdminFlags();
    };
    window.addEventListener('pageshow', loadBridgeAdminFlags);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pageshow', loadBridgeAdminFlags);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadBridgeAdminFlags]);

  useEffect(() => {
    if (!effectiveSessionId) {
      setBridgeUnlocked(false);
      return;
    }
    if (!SKIP_BILLING_GATE && !isOwnerAdmin && !bridgeUiEligible) {
      setBridgeUnlocked(false);
      return;
    }
    fetchBridgeUnlockForSession(effectiveSessionId);
  }, [effectiveSessionId, isOwnerAdmin, bridgeUiEligible, fetchBridgeUnlockForSession]);

  useEffect(() => {
    if (!showBridgeToggle) return undefined;
    loadHostBridgeStatus();
    const id = window.setInterval(loadHostBridgeStatus, 8000);
    return () => window.clearInterval(id);
  }, [showBridgeToggle, loadHostBridgeStatus]);

  const toggleBridgeUnlock = async () => {
    if (!effectiveSessionId) return;
    if (!SKIP_BILLING_GATE && !isOwnerAdmin && !bridgeUiEligible) return;
    const next = !bridgeUnlocked;
    try {
      const t = readStoredToken();
      await axios.put(
        `${API_URL}/api/chat/sessions/bridge-unlock`,
        { session_id: effectiveSessionId, bridge_unlocked: next },
        { headers: { Authorization: `Bearer ${t}` } }
      );
      setBridgeUnlocked(next);
      toast.success(next ? 'PC Bridge ON for this chat' : 'PC Bridge OFF for this chat');
      if (next) {
        const up = await loadHostBridgeStatus();
        if (up === false) {
          toast.warning(
            'Host Bridge app is not reachable from the server. Start "Open Chronicle Bridge.cmd" (Node on port 3001).',
            { duration: 12000 }
          );
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update PC Bridge');
    }
  };

  if (!showBridgeToggle) return null;

  const hostSummary =
    hostBridge.ok === true
      ? 'Host Bridge app (port 3001): reachable.'
      : hostBridge.ok === false
        ? `Host Bridge app: not reachable — run Open Chronicle Bridge.cmd (${hostBridge.error || 'connection failed'}).`
        : 'Host Bridge app: checking…';

  const diskSuffixHint =
    bridgeUnlocked && hostBridge.ok === true
      ? ' For disk tools, end your message with the word chronicle.'
      : '';

  const inner = (
    <button
      type="button"
      onClick={toggleBridgeUnlock}
      className={`mode-toggle-btn bridge-toggle-btn bridge-toggle-btn--icon-only ${bridgeUnlocked ? 'active' : ''}`}
      aria-pressed={bridgeUnlocked}
      aria-label={
        bridgeUnlocked
          ? 'PC Bridge on for this chat. Click to turn off.'
          : 'PC Bridge off for this chat. Click to turn on.'
      }
      title={
        (bridgeUnlocked
          ? 'PC Bridge permission ON for this chat — disk tools allowed when the Host Bridge app is running. Click to turn OFF.'
          : 'PC Bridge permission OFF for this chat. Click to turn ON (requires Host Bridge on your PC).') +
        ' ' +
        hostSummary +
        diskSuffixHint
      }
      data-testid="bridge-unlock-toggle"
    >
      <HardDrive size={18} />
    </button>
  );

  const wrap = <div className="bridge-unlock-with-host-status bridge-unlock-compact">{inner}</div>;

  if (wrapperClassName) {
    return <div className={wrapperClassName}>{wrap}</div>;
  }
  return wrap;
}

/** Builder home — circuit brain (blue idle, purple + lights when thinking). */
function ChronicleBrainHero({ thinking = false }) {
  return (
    <div
      className={`chronicle-brain-hero ${thinking ? 'chronicle-brain-hero--thinking' : ''}`}
      data-testid="chronicle-brain-hero"
      aria-hidden={!thinking}
    >
      <div className="chronicle-brain-hero__glow" />
      <div className="chronicle-brain-hero__pulse" />
      <img
        className="chronicle-brain-hero__img"
        src={`${process.env.PUBLIC_URL || ''}/chronicle-brain-hero.png`}
        alt=""
      />
      <svg className="chronicle-brain-hero__circuits" viewBox="0 0 280 220" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="chronicleCircuitBlue" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#67e8f9" />
            <stop offset="50%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#2563eb" />
          </linearGradient>
          <linearGradient id="chronicleCircuitFire" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#c084fc">
              <animate attributeName="offset" values="0;0.35;0.7;1;0" dur="1.1s" repeatCount="indefinite" />
            </stop>
            <stop offset="35%" stopColor="#ef4444">
              <animate attributeName="offset" values="0.15;0.5;0.85;1;0.15" dur="1.1s" repeatCount="indefinite" />
            </stop>
            <stop offset="50%" stopColor="#f472b6">
              <animate attributeName="offset" values="0.25;0.55;0.8;1;0.25" dur="1.1s" repeatCount="indefinite" />
            </stop>
            <stop offset="70%" stopColor="#f97316">
              <animate attributeName="offset" values="0.3;0.65;1;0.3;0.3" dur="1.1s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#a855f7">
              <animate attributeName="offset" values="0.5;0.85;1;0.5;0.5" dur="1.1s" repeatCount="indefinite" />
            </stop>
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from="-1 0"
              to="1 0"
              dur="0.55s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
        <path className="circuit-a" d="M140 42 L140 10 M140 42 L172 22 M140 42 L108 22" />
        <path className="circuit-b" d="M68 98 L18 98 L18 52 M212 98 L262 98 L262 52" />
        <path className="circuit-c" d="M88 158 L30 192 L30 210 M192 158 L250 192 L250 210" />
        <line className="circuit-d" x1="140" y1="168" x2="140" y2="210" />
        <line className="circuit-e" x1="112" y1="152" x2="78" y2="180" />
        <line className="circuit-f" x1="168" y1="152" x2="202" y2="180" />
        <path className="circuit-g" d="M100 75 L55 60 L40 40 M180 75 L225 60 L240 40" />
        <path className="circuit-h" d="M125 125 L95 115 L75 100 M155 125 L185 115 L205 100" />
      </svg>
    </div>
  );
}

/** Project picker — "{Name}'s Project" + Create new project (Emergent-style). */
function ChronicleProjectPicker({
  user,
  projects,
  activeProjectId,
  open,
  onToggle,
  onSelect,
  onCreate,
  menuRef,
}) {
  const active = projects.find((p) => p.id === activeProjectId) || projects[0];
  const label = active?.name || defaultProjectLabel(user);
  const firstName = firstNameFromUser(user);

  return (
    <div className="chronicle-project-picker" ref={menuRef}>
      <button
        type="button"
        className={`chronicle-project-picker__trigger ${open ? 'open' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
        data-testid="chronicle-project-picker"
      >
        <span className="chronicle-project-picker__avatar" aria-hidden />
        <span className="chronicle-project-picker__label">{label}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open ? (
        <div className="chronicle-project-picker__menu" role="menu">
          {projects.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              className={`chronicle-project-picker__row ${p.id === activeProjectId ? 'active' : ''}`}
              onClick={() => onSelect(p.id)}
            >
              <span className="chronicle-project-picker__avatar chronicle-project-picker__avatar--sm" aria-hidden />
              <span className="chronicle-project-picker__row-text">
                <strong>{p.name}</strong>
                <span>Owner · 1 member</span>
              </span>
              {p.id === activeProjectId ? <Check size={18} className="chronicle-project-picker__check" /> : null}
            </button>
          ))}
          <div className="chronicle-project-picker__divider" />
          <button
            type="button"
            role="menuitem"
            className="chronicle-project-picker__create"
            onClick={onCreate}
            data-testid="chronicle-create-project"
          >
            <Plus size={18} />
            Create new project
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Top-left mini preview — what you built / are building (Emergent-style). */
function BuilderPreviewDock({
  hidden,
  collapsed,
  onClose,
  onToggleCollapse,
  previewStatus,
  previewIframeKey,
  previewBuilding,
  previewViewable,
  previewAppUrl,
  onRefresh,
  onOpenExternal,
  onRunBuild,
}) {
  if (hidden) return null;

  const showIframe = previewViewable && previewAppUrl && !previewBuilding;

  return (
    <div
      className={`builder-preview-dock ${collapsed ? 'builder-preview-dock--collapsed' : ''}`}
      data-testid="builder-preview-dock"
    >
      <div className="builder-preview-dock__header">
        <span className="builder-preview-dock__title">
          <span
            className={`builder-preview-dock__dot ${
              previewBuilding ? 'builder-preview-dock__dot--build' : showIframe ? 'builder-preview-dock__dot--live' : ''
            }`}
          />
          Your build
        </span>
        <div className="builder-preview-dock__header-actions">
          <button
            type="button"
            className="builder-preview-dock__icon-btn"
            title={collapsed ? 'Expand dock' : 'Shrink dock'}
            onClick={onToggleCollapse}
          >
            {collapsed ? <Eye size={16} /> : <ChevronDown size={16} />}
          </button>
          <button type="button" className="builder-preview-dock__icon-btn" title="Hide preview" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="builder-preview-dock__stage">
        {showIframe ? (
          <iframe
            key={previewIframeKey}
            className="builder-preview-dock__iframe"
            src={previewAppUrl}
            title="Build preview"
            data-testid="builder-preview-dock-iframe"
          />
        ) : (
          <div className="builder-preview-dock__placeholder">
            {previewBuilding ? (
              <>
                <strong>Building…</strong>
                <span>{previewStatus?.build_summary || 'Compiling your app'}</span>
              </>
            ) : (
              <>
                <strong>No preview yet</strong>
                <span>Chat to build, then tap Build preview</span>
              </>
            )}
          </div>
        )}
      </div>
      <div className="builder-preview-dock__footer">
        <div className="builder-preview-dock__footer-group">
          <button type="button" className="builder-preview-dock__icon-btn" title="Refresh" onClick={onRefresh}>
            <RefreshCw size={15} />
          </button>
          <button
            type="button"
            className="builder-preview-dock__icon-btn"
            title="Open in new tab"
            onClick={onOpenExternal}
            disabled={!previewAppUrl}
          >
            <ExternalLink size={15} />
          </button>
        </div>
        <button
          type="button"
          className="builder-preview-dock__expand-btn"
          onClick={showIframe ? onOpenExternal : onRunBuild}
          disabled={previewBuilding}
        >
          {showIframe ? 'Open app' : previewBuilding ? 'Building…' : 'Build preview'}
        </button>
      </div>
    </div>
  );
}

// Chat Component
const ChatView = ({ onBridgeSessionIdChange, sidebarCollapsed = false, onToggleSidebar }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => createInitialChatSessionId());
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [appBuilderMode, setAppBuilderMode] = useState(false);
  const [builderTier, setBuilderTier] = useState('balanced');
  const [chatQuality, setChatQuality] = useState('auto');
  const [chatMode, setChatMode] = useState('agent');
  const [chatVoice, setChatVoice] = useState(() => {
    try { return localStorage.getItem('chronicle_chat_voice') || ''; } catch { return ''; }
  });
  const [availableVoices, setAvailableVoices] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = React.useRef(null);
  const [sbBrains, setSbBrains] = useState([]);
  const [sbProvider, setSbProvider] = useState('');
  const [sbModel, setSbModel] = useState('');
  // Compact UI preference (stored locally per browser profile)
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem('chronicle_compact') === '1'; } catch { return false; }
  });
  const [uiTheme, setUiTheme] = useState(() => {
    try { return localStorage.getItem(CHRONICLE_THEME_KEY) === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
  });
  const [builderProductTab, setBuilderProductTab] = useState(() => {
    try {
      const v = localStorage.getItem(CHRONICLE_BUILDER_TAB_KEY);
      return BUILDER_PRODUCT_TABS.some((t) => t.id === v) ? v : 'fullstack';
    } catch {
      return 'fullstack';
    }
  });
  const [headerMoreOpen, setHeaderMoreOpen] = useState(false);
  const headerMoreRef = useRef(null);
  const [showBuilderTierPicker, setShowBuilderTierPicker] = useState(false);
  const [showHostingModal, setShowHostingModal] = useState(false);
  const [hostingPrices, setHostingPrices] = useState({ monthly: 12, yearly: 115.2 });
  const [hostingCheckoutLoading, setHostingCheckoutLoading] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [webSearchAvailable, setWebSearchAvailable] = useState(false);
  const [webSearchProviders, setWebSearchProviders] = useState([]);
  const [webSearchProvider, setWebSearchProvider] = useState(() => {
    try { return localStorage.getItem(CHRONICLE_WEB_SEARCH_PROVIDER_KEY) || 'brave'; } catch { return 'brave'; }
  });
  const [showChatSwitcher, setShowChatSwitcher] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [creditStatus, setCreditStatus] = useState({ credits: 0, warning_threshold: 20, warning_message: '' });
  const { user, token } = useAuth();
  const messagesEndRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);
  const abortControllerRef = React.useRef(null);
  const [previewStatus, setPreviewStatus] = useState(null);
  const previewStatusRef = useRef(null);
  const previewSyncToastShownRef = useRef(false);
  const [previewIframeKey, setPreviewIframeKey] = useState(0);
  const [previewActionLoading, setPreviewActionLoading] = useState(false);
  const [previewDockHidden, setPreviewDockHidden] = useState(() => {
    try { return localStorage.getItem(CHRONICLE_PREVIEW_DOCK_HIDDEN_KEY) === '1'; } catch { return false; }
  });
  const [previewDockCollapsed, setPreviewDockCollapsed] = useState(true);
  const [builderProjects, setBuilderProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const projectMenuRef = useRef(null);

  const newChatSessionId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);

  useEffect(() => {
    const loadAdmin = async () => {
      try {
        const t = readStoredToken();
        if (!t) {
          setIsOwnerAdmin(false);
          return;
        }
        const res = await axios.get(`${API_URL}/api/user/is-admin`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setIsOwnerAdmin(!!res.data?.is_admin);
      } catch {
        setIsOwnerAdmin(false);
      }
    };
    loadAdmin();
  }, [user?.id, token]);

  useEffect(() => {
    onBridgeSessionIdChange?.(sessionId);
  }, [sessionId, onBridgeSessionIdChange]);

  // Persist compact preference
  useEffect(() => {
    try { localStorage.setItem('chronicle_compact', compact ? '1' : '0'); } catch {}
  }, [compact]);

  // React to external compact changes (cross-view sync)
  useEffect(() => {
    const onCompactChange = (e) => {
      try {
        const v = String((e && e.detail) ?? localStorage.getItem('chronicle_compact')) === '1';
        setCompact(v);
      } catch {}
    };
    window.addEventListener('chronicle:compact-change', onCompactChange);
    return () => window.removeEventListener('chronicle:compact-change', onCompactChange);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(CHRONICLE_THEME_KEY, uiTheme); } catch {}
  }, [uiTheme]);

  useEffect(() => {
    const onDoc = (e) => {
      if (headerMoreRef.current && !headerMoreRef.current.contains(e.target)) setHeaderMoreOpen(false);
      if (projectMenuRef.current && !projectMenuRef.current.contains(e.target)) setProjectMenuOpen(false);
    };
    if (headerMoreOpen || projectMenuOpen) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [headerMoreOpen, projectMenuOpen]);

  useEffect(() => {
    if (!user?.id) return;
    const key = chronicleProjectsKey(user.id);
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.projects?.length) {
          setBuilderProjects(data.projects);
          setActiveProjectId(data.activeProjectId || data.projects[0].id);
          return;
        }
      }
    } catch { /* fresh */ }
    const id = newProjectId();
    const initial = [{ id, name: defaultProjectLabel(user), sessionId: sessionId }];
    setBuilderProjects(initial);
    setActiveProjectId(id);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !activeProjectId || builderProjects.length === 0) return;
    try {
      localStorage.setItem(
        chronicleProjectsKey(user.id),
        JSON.stringify({ activeProjectId, projects: builderProjects })
      );
    } catch { /* ignore */ }
  }, [user?.id, activeProjectId, builderProjects]);

  useEffect(() => {
    if (!activeProjectId || !sessionId) return;
    setBuilderProjects((prev) =>
      prev.map((p) => (p.id === activeProjectId ? { ...p, sessionId } : p))
    );
  }, [sessionId, activeProjectId]);

  const selectBuilderProject = async (projectId) => {
    if (projectId === activeProjectId) {
      setProjectMenuOpen(false);
      return;
    }
    setBuilderProjects((prev) =>
      prev.map((p) => (p.id === activeProjectId ? { ...p, sessionId } : p))
    );
    const target = builderProjects.find((p) => p.id === projectId);
    setActiveProjectId(projectId);
    setProjectMenuOpen(false);
    if (target?.sessionId) {
      await loadChatSession(target.sessionId);
    } else {
      const sid = newChatSessionId();
      setBuilderProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, sessionId: sid } : p))
      );
      setMessages([]);
      setSessionId(sid);
    }
  };

  const createBuilderProject = () => {
    const suggested = `Project ${builderProjects.length + 1}`;
    const name = window.prompt('Name your new project', suggested)?.trim() || suggested;
    const id = newProjectId();
    const sid = newChatSessionId();
    setBuilderProjects((prev) => [...prev, { id, name, sessionId: sid }]);
    setActiveProjectId(id);
    setMessages([]);
    setSessionId(sid);
    setProjectMenuOpen(false);
    toast.success(`Project “${name}” created`);
  };

  const builderFirstName = firstNameFromUser(user);

  const selectBuilderProductTab = (id) => {
    setBuilderProductTab(id);
    try { localStorage.setItem(CHRONICLE_BUILDER_TAB_KEY, id); } catch {}
    if (id === 'brainstorm') {
      setAppBuilderMode(false);
      setChatMode('plan');
    } else {
      setAppBuilderMode(true);
      setChatMode('agent');
    }
  };

  const activeBuilderTab = BUILDER_PRODUCT_TABS.find((t) => t.id === builderProductTab) || BUILDER_PRODUCT_TABS[0];
  const composerPlaceholder = activeBuilderTab.placeholder;

  const previewBuilding = previewStatus?.status === 'building';
  const previewViewable = !!previewStatus?.viewable;
  const previewAppUrl = previewStatus?.preview_url || null;

  useEffect(() => {
    previewStatusRef.current = previewStatus;
  }, [previewStatus]);

  const refreshPreviewStatus = useCallback(async () => {
    const t = readStoredToken();
    if (!t) return null;
    try {
      const res = await axios.get(`${API_URL}/api/preview/status`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setPreviewStatus(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  const runPreviewBuild = useCallback(async () => {
    const t = readStoredToken();
    if (!t) return;
    setPreviewActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/preview/build`, {}, { headers: { Authorization: `Bearer ${t}` } });
      toast.info('Building preview…');
      await refreshPreviewStatus();
    } catch (err) {
      toast.error(
        apiErrorDetail(err, {
          provider: sbProvider,
          model: sbModel,
          appBuilderMode,
          builderTier,
        }) || 'Preview build failed'
      );
    } finally {
      setPreviewActionLoading(false);
    }
  }, [refreshPreviewStatus, sbProvider, sbModel, appBuilderMode, builderTier]);

  const runPreviewUpdate = useCallback(async () => {
    const t = readStoredToken();
    if (!t) return;
    setPreviewActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/preview/update`, {}, { headers: { Authorization: `Bearer ${t}` } });
      toast.info('Updating preview…');
      await refreshPreviewStatus();
    } catch (err) {
      toast.error(
        apiErrorDetail(err, {
          provider: sbProvider,
          model: sbModel,
          appBuilderMode,
          builderTier,
        }) || 'Preview update failed'
      );
    } finally {
      setPreviewActionLoading(false);
    }
  }, [refreshPreviewStatus, sbProvider, sbModel, appBuilderMode, builderTier]);

  const waitForPreviewReady = useCallback(async (maxMs = 120000) => {
    const started = Date.now();
    let st = await refreshPreviewStatus();
    while (st && Date.now() - started < maxMs) {
      if (st.viewable && st.preview_url) return st;
      if (st.status === 'build_failed' || st.status === 'no_package' || st.status === 'empty') return st;
      if (st.status !== 'building') break;
      await new Promise((r) => setTimeout(r, 2500));
      st = await refreshPreviewStatus();
    }
    return st;
  }, [refreshPreviewStatus]);

  const syncBuilderPreview = useCallback(async () => {
    const t = readStoredToken();
    if (!t) return null;
    setPreviewActionLoading(true);
    previewSyncToastShownRef.current = false;
    try {
      toast.info('Syncing builder files and building preview…');
      const headers = { Authorization: `Bearer ${t}` };
      await axios.post(`${API_URL}/api/preview/ensure`, {}, { headers });
      let st = await refreshPreviewStatus();
      if (!st) {
        toast.error('Preview status check failed — sign in again or refresh the page.');
        previewSyncToastShownRef.current = true;
        return null;
      }
      if (st.status === 'empty' || st.status === 'no_package') {
        try {
          await axios.post(`${API_URL}/api/preview/scaffold`, {}, { headers });
        } catch {
          /* workspace may already exist */
        }
      }
      try {
        await axios.post(`${API_URL}/api/preview/update`, {}, { headers });
      } catch (updateErr) {
        if (updateErr?.response?.status === 409) {
          toast.info('Preview build already running — waiting for it to finish…');
        } else {
          throw updateErr;
        }
      }
      st = await waitForPreviewReady();
      if (st?.viewable && st?.preview_url) {
        toast.success('Preview built — open View to see your app.');
      } else if (st?.status === 'building') {
        toast.info('Preview is still building. Click View again in a minute.');
      } else if (st?.status === 'build_failed') {
        const tail = (st.last_build_log_tail || '').trim();
        toast.error(tail ? `Build failed: ${tail.slice(0, 220)}` : 'Preview build failed.');
      } else {
        toast.info(
          'Starter preview only? In Builder chat ask to save your app (e.g. save this as src/App.jsx), then Preview again.'
        );
      }
      return st || null;
    } catch (err) {
      const msg =
        apiErrorDetail(err, {
          provider: sbProvider,
          model: sbModel,
          appBuilderMode,
          builderTier,
        }) || 'Preview sync failed';
      toast.error(msg);
      previewSyncToastShownRef.current = true;
      return null;
    } finally {
      setPreviewActionLoading(false);
    }
  }, [refreshPreviewStatus, waitForPreviewReady, sbProvider, sbModel, appBuilderMode, builderTier]);

  const openBuilderView = useCallback(async () => {
    if (previewActionLoading) return;
    const existing = await refreshPreviewStatus();
    if (existing?.viewable && existing?.preview_url) {
      window.open(existing.preview_url, '_blank', 'noopener,noreferrer');
      return;
    }
    const st = await syncBuilderPreview();
    if (!st) {
      if (!previewSyncToastShownRef.current) {
        const retry = await refreshPreviewStatus();
        if (retry?.viewable && retry?.preview_url) {
          window.open(retry.preview_url, '_blank', 'noopener,noreferrer');
          return;
        }
        toast.error(retry?.build_summary || 'Preview not ready — wait for build or check Menu → Subscription credits.');
      }
      return;
    }
    const url = st?.preview_url || '';
    if (st?.viewable && url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (st?.status === 'build_failed') {
      const tail = (st.last_build_log_tail || st.build_summary || '').trim();
      toast.error(tail ? `Build failed: ${tail.slice(0, 240)}` : 'Preview build failed — check Builder chat or try Preview again.');
      return;
    }
    if (st?.status === 'no_package' || st?.status === 'empty') {
      toast.error(st.build_summary || 'Add app files or Scaffold first, then Preview → View.');
      return;
    }
    toast.error(st.build_summary || 'Preview not ready yet — wait for build, then View again.');
  }, [syncBuilderPreview, waitForPreviewReady, previewActionLoading, refreshPreviewStatus]);

  useEffect(() => {
    try { localStorage.setItem(CHRONICLE_PREVIEW_DOCK_HIDDEN_KEY, previewDockHidden ? '1' : '0'); } catch {}
  }, [previewDockHidden]);

  useEffect(() => {
    if (!appBuilderMode) return;
    refreshPreviewStatus();
  }, [appBuilderMode, user?.id, refreshPreviewStatus]);

  useEffect(() => {
    if (!appBuilderMode || previewDockHidden) return undefined;
    const id = setInterval(() => {
      refreshPreviewStatus();
    }, 2500);
    return () => clearInterval(id);
  }, [appBuilderMode, previewBuilding, previewDockHidden, refreshPreviewStatus]);

  const handlePreviewHint = useCallback(
    (hint) => {
      if (!hint || !appBuilderMode) return;
      refreshPreviewStatus();
      if (hint.needs_update) {
        toast.info('Saved — update preview to see changes', {
          action: { label: 'Update', onClick: () => runPreviewUpdate() },
        });
      } else if (hint.suggest_build) {
        toast.info('Build to preview your app', {
          action: { label: 'Preview', onClick: () => syncBuilderPreview() },
        });
      } else if (hint.viewable && hint.preview_url) {
        toast.success('Preview ready', {
          action: { label: 'View', onClick: () => openBuilderView() },
        });
      }
    },
    [appBuilderMode, refreshPreviewStatus, runPreviewUpdate, syncBuilderPreview]
  );

  // Force scroll to top on mount - use multiple methods
  useEffect(() => {
    // Immediate scroll
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    
    // After render
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
    
    // After short delay
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 50);
    
    // After longer delay for slow renders
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 200);
  }, []);

  useEffect(() => {
    if (!showHostingModal) return;
    axios
      .get(`${API_URL}/api/hosting/pricing`)
      .then((r) => {
        setHostingPrices({
          monthly: r.data.monthly,
          yearly: r.data.yearly
        });
      })
      .catch(() => {});
  }, [showHostingModal]);

  const startHostingCheckout = async (interval) => {
    if (SKIP_BILLING_GATE) {
      toast.message('Local dev: hosting checkout is off.');
      return;
    }
    setHostingCheckoutLoading(true);
    try {
      const token = readStoredToken();
      const origin =
        (typeof process !== 'undefined' &&
          process.env.REACT_APP_PUBLIC_APP_URL &&
          String(process.env.REACT_APP_PUBLIC_APP_URL).trim()) ||
        window.location.origin;
      const res = await axios.post(
        `${API_URL}/api/hosting/checkout`,
        { interval, origin_url: origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Could not start hosting checkout');
    } finally {
      setHostingCheckoutLoading(false);
    }
  };

  // Fetch chat sessions for switcher
  const fetchChatSessions = async () => {
    try {
      const token = readStoredToken();
      const res = await axios.get(`${API_URL}/api/chat/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setChatSessions(res.data.sessions || []);
    } catch (err) {
      console.error('Failed to load chat sessions');
    }
  };

  // Load a specific chat session
  const loadChatSession = async (targetSessionId) => {
    try {
      const token = readStoredToken();
      const historyRes = await axios.get(`${API_URL}/api/chat/history?session_id=${targetSessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const conversations = historyRes.data.conversations;
      
      if (conversations && conversations.length > 0) {
        const loadedMessages = [];
        conversations.reverse().forEach(conv => {
          loadedMessages.push({ role: 'user', content: conv.user_message });
          loadedMessages.push({ role: 'assistant', content: conv.assistant_response });
        });
        setMessages(loadedMessages);
        setSessionId(targetSessionId);
      } else {
        setMessages([]);
        setSessionId(targetSessionId);
      }
      setShowChatSwitcher(false);
    } catch (err) {
      toast.error('Failed to load chat');
    }
  };

  // Start new chat
  const startNewChat = () => {
    setMessages([]);
    setSessionId(newChatSessionId());
    setShowChatSwitcher(false);
  };

  // Check if web search is available (enabled by admin)
  useEffect(() => {
    const checkWebSearch = async () => {
      const token = readStoredToken();
      if (!token) {
        setWebSearchAvailable(false);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/settings/web-search-available`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWebSearchAvailable(res.data.available);
        const provs = Array.isArray(res.data.providers) ? res.data.providers : [];
        setWebSearchProviders(provs);
        if (provs.length && !provs.includes(webSearchProvider)) {
          setWebSearchProvider(provs[0]);
        }
      } catch (err) {
        setWebSearchAvailable(false);
      }
    };
    checkWebSearch();
  }, []);

  const fetchCreditStatus = async () => {
    const token = readStoredToken();
    if (!token) {
      setCreditStatus({ credits: 0, warning_threshold: 20, warning_message: '' });
      return null;
    }
    try {
      const res = await axios.get(`${API_URL}/api/user/credit-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditStatus(res.data || { credits: 0, warning_threshold: 20, warning_message: '' });
      return res.data;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    fetchCreditStatus();
  }, []);

  const persistChatPrefs = async (quality, mode) => {
    try {
      const t = readStoredToken();
      if (!t) return;
      await axios.post(
        `${API_URL}/api/user/settings`,
        { chat_quality: quality, chat_mode: mode },
        { headers: { Authorization: `Bearer ${t}` } }
      );
    } catch (e) {
      console.warn('persistChatPrefs', e);
    }
  };

  useEffect(() => {
    const loadChatPrefs = async () => {
      const t = readStoredToken();
      if (!t) return;
      try {
        const res = await axios.get(`${API_URL}/api/user/settings`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        const s = res.data?.settings || {};
        if (['auto', 'fast', 'best'].includes(s.chat_quality)) {
          setChatQuality(s.chat_quality);
        }
        if (['agent', 'plan', 'ask'].includes(s.chat_mode)) {
          setChatMode(s.chat_mode);
        } else if (s.chat_mode === 'full') {
          setChatMode('agent');
        }
      } catch (_) {
        /* keep defaults */
      }
    };
    loadChatPrefs();
  }, [user?.id, token]);

  // Fetch available TTS voices
  useEffect(() => {
    const loadVoices = async () => {
      try {
        const token = readStoredToken();
        if (!token) return;
        const res = await axios.get(`${API_URL}/api/voices/available`, { headers: { 'Authorization': `Bearer ${token}` } });
        setAvailableVoices(res.data?.voices || []);
      } catch {}
    };
    loadVoices();
  }, []);

  const [sbModelsByProvider, setSbModelsByProvider] = useState({});

  // Load brains from Chronicle API (Switchboard vault handoff) — retries until found
  const loadSbBrains = React.useCallback(async () => {
    const t = readStoredToken();
    if (!t) return false;
    try {
      const res = await axios.get(`${API_URL}/api/chat/brains`, {
        headers: { Authorization: `Bearer ${t}` },
        timeout: API_REQUEST_TIMEOUT_MS,
      });
      const data = res.data || {};
      const brains = (data.brains || []).filter(
        (b) => !['serper', 'brave'].includes(String(b.provider || '').toLowerCase())
      );
      if (data.models && typeof data.models === 'object') {
        setSbModelsByProvider(data.models);
      }
      if (brains.length > 0) {
        setSbBrains(brains);
        const active = data.active || {};
        const activeProv = String(active.provider || '').toLowerCase();
        if (active.provider && !['serper', 'brave'].includes(activeProv)) {
          setSbProvider(active.provider);
          if (active.model) setSbModel(active.model);
        } else {
          const first = brains.find((b) => b.has_key) || brains[0];
          setSbProvider(first.provider);
          setSbModel(first.model || '');
        }
        return true;
      }
    } catch {
      /* vault not synced yet */
    }
    return false;
  }, []);

  useEffect(() => {
    let timer;
    const attempt = async () => {
      const ok = await loadSbBrains();
      if (!ok) timer = setTimeout(attempt, 5000);
    };
    attempt();
    return () => clearTimeout(timer);
  }, [loadSbBrains]);

  // Persist voice preference
  useEffect(() => {
    try { localStorage.setItem('chronicle_chat_voice', chatVoice); } catch {}
  }, [chatVoice]);

  const [showFileCommands, setShowFileCommands] = useState(false);
  const [userFiles, setUserFiles] = useState([]);
  const [showFilesList, setShowFilesList] = useState(false);

  const fileCommands = [
    { label: 'Show my files', action: 'showFiles' },
    { label: 'Save this as...', command: 'save this as ', prompt: true },
    { label: 'Open file...', command: 'open ', prompt: true },
    { label: 'Delete file...', command: 'delete file ', prompt: true },
    { label: 'Hold (paste + hold)', command: '', info: 'Add "hold" at the end of your message to store it' },
  ];

  const fetchUserFiles = async () => {
    try {
      const token = readStoredToken();
      const res = await axios.get(`${API_URL}/api/files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserFiles(res.data.files || []);
    } catch (err) {
      console.error('Failed to fetch files');
      setUserFiles([]);
    }
  };

  const handleFileCommand = (cmd) => {
    if (cmd.action === 'showFiles') {
      fetchUserFiles();
      setShowFilesList(true);
      setShowFileCommands(false);
      return;
    }
    if (cmd.info) {
      toast.info(cmd.info);
      setShowFileCommands(false);
      return;
    }
    setInput(cmd.command);
    setShowFileCommands(false);
    if (!cmd.prompt) {
      setTimeout(() => {
        document.querySelector('[data-testid="send-button"]')?.click();
      }, 100);
    }
  };

  const handleOpenFile = (filename) => {
    setInput(`open ${filename}`);
    setShowFilesList(false);
    setTimeout(() => {
      document.querySelector('[data-testid="send-button"]')?.click();
    }, 100);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load last session on mount
  useEffect(() => {
    const loadLastSession = async () => {
      try {
        const token = readStoredToken();
        if (!token) {
          setLoadingHistory(false);
          return;
        }
        
        // Always fetch latest session from server (enables multi-device sync)
        const sessionsRes = await axios.get(`${API_URL}/api/chat/sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const sessions = sessionsRes.data.sessions;
        
        if (sessions && sessions.length > 0) {
          // Always use the most recent session from server
          const targetSession = sessions[0]._id;
          
          // Fetch messages for this session
          const historyRes = await axios.get(`${API_URL}/api/chat/history?session_id=${targetSession}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const conversations = historyRes.data.conversations;
          
          if (conversations && conversations.length > 0) {
            // Convert to message format
            const loadedMessages = [];
            conversations.reverse().forEach(conv => {
              loadedMessages.push({ role: 'user', content: conv.user_message });
              loadedMessages.push({ role: 'assistant', content: conv.assistant_response });
            });
            
            setMessages(loadedMessages);
            setSessionId(targetSession);
          } else {
            // Session exists but no rows yet — still need session id or Bridge toggle never mounts
            setMessages([]);
            setSessionId(targetSession);
          }
        } else {
          setSessionId(newChatSessionId());
        }
      } catch (err) {
        console.log('No previous session found', err);
        setSessionId(newChatSessionId());
      } finally {
        setLoadingHistory(false);
      }
    };
    
    // Small delay to ensure token is available
    setTimeout(() => {
      loadLastSession();
    }, 100);
  }, []);

  // Refresh session when window gains focus (helps with multi-device sync)
  useEffect(() => {
    let isMounted = true;
    
    const handleFocus = async () => {
      if (!isMounted) return;
      const token = readStoredToken();
      if (!token) return;
      
      try {
        const sessionsRes = await axios.get(`${API_URL}/api/chat/sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const sessions = sessionsRes.data.sessions;
        
        if (sessions && sessions.length > 0 && isMounted) {
          const latestSessionId = sessions[0]._id;
          
          // Only reload if different session
          if (latestSessionId !== sessionId) {
            const historyRes = await axios.get(`${API_URL}/api/chat/history?session_id=${latestSessionId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const conversations = historyRes.data.conversations;
            
            if (conversations && conversations.length > 0 && isMounted) {
              const loadedMessages = [];
              conversations.reverse().forEach(conv => {
                loadedMessages.push({ role: 'user', content: conv.user_message });
                loadedMessages.push({ role: 'assistant', content: conv.assistant_response });
              });
              setMessages(loadedMessages);
              setSessionId(latestSessionId);
            }
          }
        }
      } catch (err) {
        console.log('Focus refresh failed');
      }
    };
    
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') handleFocus();
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      isMounted = false;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        setPreviewUrl(URL.createObjectURL(file));
        toast.info('Image attached — press Send image or add an optional note');
      }
    }
  };

  // Handle camera capture
  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      toast.success('Photo ready — press Send image or add an optional note');
    }
  };

  // Clear selected file
  const clearFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // Find the last line (from the bottom) that is an automation command — model may add a blank line or filler after
  const findActionLine = (response, prefixChecks) => {
    const lines = response.split('\n');
    for (let i = lines.length - 1; i >= 0; i -= 1) {
      const t = lines[i].trim();
      for (const p of prefixChecks) {
        if (t.startsWith(p)) return t;
      }
    }
    return null;
  };

  // Strip command lines from what we show in the thread (keeps the visible reply clean)
  const stripActionLines = (text) => {
    if (!text) return text;
    return text
      .split('\n')
      .filter((line) => {
        const t = line.trim();
        if (t.startsWith('SEND_SMS_TWILIO|') || t.startsWith('SEND_SMS_NATIVE|')) return false;
        if (t.startsWith('MAKE_CALL_TWILIO|') || t.startsWith('MAKE_CALL_NATIVE|')) return false;
        return true;
      })
      .join('\n')
      .trim();
  };

  // Execute actions from Chronicle's response (SMS/Call)
  const executeActionsFromResponse = async (response) => {
    const token = readStoredToken();

    // Check for SMS action: SEND_SMS_TWILIO|phone|message or SEND_SMS_NATIVE|phone|message
    const smsLine = findActionLine(response, ['SEND_SMS_TWILIO|', 'SEND_SMS_NATIVE|']);
    if (smsLine) {
      const parts = smsLine.split('|');
      if (parts.length >= 3) {
        const useNative = smsLine.startsWith('SEND_SMS_NATIVE');
        const phone = parts[1];
        const message = parts.slice(2).join('|');
        try {
          const res = await axios.post(`${API_URL}/api/sms/send`, {
            to: phone,
            message: message,
            use_native: useNative
          }, { headers: { 'Authorization': `Bearer ${token}` } });
          if (res.data.native && res.data.link) {
            window.location.href = res.data.link;
          } else if (res.data.success) {
            toast.success('SMS sent via Twilio!');
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to send SMS');
        }
      }
    }

    // Check for Call action: MAKE_CALL_TWILIO|phone|message or MAKE_CALL_NATIVE|phone
    const callLine = findActionLine(response, ['MAKE_CALL_TWILIO|', 'MAKE_CALL_NATIVE|']);
    if (callLine) {
      const parts = callLine.split('|');
      if (parts.length >= 2) {
        const useNative = callLine.startsWith('MAKE_CALL_NATIVE');
        const phone = parts[1];
        const message = parts[2] || '';
        try {
          const res = await axios.post(`${API_URL}/api/call/place`, {
            to: phone,
            message: message,
            use_native: useNative
          }, { headers: { 'Authorization': `Bearer ${token}` } });
          if (res.data.native && res.data.link) {
            window.location.href = res.data.link;
          } else if (res.data.success) {
            toast.success('Call initiated via Twilio!');
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to place call');
        }
      }
    }
  };

  // Text-to-speech using ElevenLabs
  const speakMessage = async (text, voiceId) => {
    if (isSpeaking) {
      // Stop current audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsSpeaking(false);
      return;
    }

    setIsSpeaking(true);
    try {
      const token = readStoredToken();
      const payload = { text };
      if (voiceId) payload.voice_id = voiceId;
      const res = await axios.post(
        `${API_URL}/api/voice/tts`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (res.data?.audio_url) {
        const audio = new Audio(res.data.audio_url);
        audioRef.current = audio;
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => {
          setIsSpeaking(false);
          toast.error('Failed to play audio');
        };
        
        audio.play();
      }
    } catch (err) {
      console.error('TTS error:', err);
      setIsSpeaking(false);
      // Fallback to browser TTS
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-GB';
        utterance.rate = 1;
        window.speechSynthesis.speak(utterance);
      } else {
        toast.error('Text-to-speech not available');
      }
    }
  };

  // Copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  // Parse code blocks in messages
  const parseMessage = (content) => {
    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'code', language: match[1] || 'text', content: match[2].trim() });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push({ type: 'text', content: content.slice(lastIndex) });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content }];
  };

  const hasImageDraft = Boolean(previewUrl || selectedFile);

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!input.trim() && !hasImageDraft) || sending) return;
    
    toast.info(selectedFile ? 'Sending image...' : 'Sending...');

    const userMessage = input.trim();
    setInput('');
    
    const newMessage = { 
      role: 'user', 
      content: userMessage,
      image: previewUrl
    };
    setMessages(prev => [...prev, newMessage]);
    setSending(true);

    try {
      if (appBuilderMode && !SKIP_BILLING_GATE) {
        const latestCreditStatus = await fetchCreditStatus();
        if (latestCreditStatus && latestCreditStatus.credit_exhausted) {
          toast.error(
            formatCreditToastMessage('exhausted', latestCreditStatus, {
              provider: sbProvider,
              model: sbModel,
              appBuilderMode,
              builderTier,
            })
          );
          setSending(false);
          setMessages(prev => prev.slice(0, -1));
          return;
        }
      }

      // If there's a file, upload it first
      let imageUrl = null;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        try {
          const token = readStoredToken();
          if (!token) {
            toast.error('Invalid token');
            return;
          }
          const uploadRes = await axios.post(`${API_URL}/api/upload`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          imageUrl = uploadRes.data.url;
          toast.success('Image uploaded');
        } catch (uploadErr) {
          toast.error('Image upload failed');
          console.log('File upload failed:', uploadErr);
          setSending(false);
          setMessages(prev => prev.slice(0, -1));
          return;
        }
      }

      if (selectedFile && !imageUrl) {
        toast.error('Image upload failed');
        setSending(false);
        setMessages(prev => prev.slice(0, -1));
        return;
      }

      const token = readStoredToken();
      if (!token) {
        toast.error('Invalid token');
        setSending(false);
        setMessages(prev => prev.slice(0, -1));
        return;
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      const res = await axios.post(`${API_URL}/api/chat`, {
        message: userMessage,
        session_id: sessionId,
        image_url: imageUrl,
        app_builder_mode: appBuilderMode,
        builder_tier: builderTier,
        web_search: webSearchEnabled,
        web_search_provider: webSearchProvider,
        chat_quality: chatQuality,
        chat_mode: chatMode,
        provider: sbProvider || undefined,
        model: sbModel || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal
      });
      
      if (!sessionId) {
        setSessionId(res.data.session_id);
      }
      
      const aiResponse = res.data.response;
      const displayReply = stripActionLines(aiResponse) || aiResponse;
      setMessages(prev => [...prev, { role: 'assistant', content: displayReply, webSearch: res.data.web_search || null }]);
      if (res.data?.credit_warning?.warning_message) {
        toast.warn(
          formatCreditToastMessage('low', res.data.credit_warning, {
            provider: sbProvider,
            model: sbModel,
            appBuilderMode,
            builderTier,
          })
        );
      }
      await fetchCreditStatus();
      if (res.data?.preview_hint) {
        handlePreviewHint(res.data.preview_hint);
      }
      
      // Check for action tags and execute them (use full model output; commands may be last lines)
      await executeActionsFromResponse(aiResponse);
      
      clearFile();
    } catch (err) {
      if (err.name === 'CanceledError' || err.message === 'canceled') {
        setMessages(prev => [...prev, { role: 'assistant', content: '[Stopped]' }]);
      } else {
        toast.error(
          apiErrorDetail(err, {
            provider: sbProvider,
            model: sbModel,
            appBuilderMode,
            builderTier,
          }) || 'Failed to send message'
        );
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setSending(false);
      abortControllerRef.current = null;
    }
  };

  // Stop the current response
  const stopResponse = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      toast.info('Response stopped');
    }
  };

  // Handle Enter key to send
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        setInput(prev => prev + text);
      }).catch(() => {});
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') return;
    if (e.ctrlKey || e.metaKey) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  // Memories panel state
  const [showMemories, setShowMemories] = useState(false);
  const [memories, setMemories] = useState([]);
  const [loadingMemories, setLoadingMemories] = useState(false);

  const fetchMemories = async () => {
    setLoadingMemories(true);
    try {
      const res = await axios.get(`${API_URL}/api/memory`);
      setMemories(res.data);
    } catch (err) {
      console.log('Failed to load memories');
    } finally {
      setLoadingMemories(false);
    }
  };

  const toggleMemories = () => {
    if (!showMemories) {
      fetchMemories();
    }
    setShowMemories(!showMemories);
  };

  const insertMemory = (memory) => {
    setInput(prev => prev + (prev ? '\n' : '') + `${memory.key}: ${memory.value}`);
    setShowMemories(false);
  };

  return (
    <div
      className={`chat-view chronicle-clean-ui ${uiTheme === 'light' ? 'chronicle-theme-light' : ''} ${appBuilderMode ? 'builder-mode builder-mode-active' : ''} ${!appBuilderMode && chatMode !== 'agent' ? 'non-agent-chat-mode' : ''} ${compact ? 'compact' : ''}`}
    >
      <div
        className={`chat-header ${sidebarCollapsed ? 'chat-header--sidebar-collapsed' : ''}`}
      >
        <div className="chat-header-titles">
          {onToggleSidebar ? (
            <button
              type="button"
              className="chronicle-sidebar-open-btn"
              onClick={onToggleSidebar}
              aria-label="Open or close menu"
              data-testid="chronicle-sidebar-toggle"
            >
              <Menu size={20} />
            </button>
          ) : null}
          <span className="chat-heading-brand">Chronicle</span>
          {appBuilderMode ? (
            <span className="chat-heading-mode-pill mode-pill-builder">Builder</span>
          ) : (
            <span className={`chat-heading-mode-pill chat-mode-pill-${chatMode}`}>
              {chatMode === 'agent' ? 'Code' : chatMode === 'plan' ? 'Plan' : 'Ask'}
            </span>
          )}
          {SKIP_BILLING_GATE && (
            <span
              className={`dev-role-badge ${isOwnerAdmin ? 'dev-role-owner' : 'dev-role-regular'}`}
              title={
                isOwnerAdmin
                  ? 'Owner admin (Bridge + Admin). Local dev defaults here — ?devRegular=1 for non-admin dev user.'
                  : 'Regular dev user — ?devOwner=1 or new tab / clear session uses owner by default on local dev.'
              }
            >
              {isOwnerAdmin ? 'Owner admin' : 'Regular'}
            </span>
          )}
          {token && (
            <button
              type="button"
              className="chat-active-brain-pill"
              title="Your AI provider and model — click to open Switchboard"
              onClick={() => openChronicleSwitchboard(isOwnerAdmin)}
              data-testid="chat-active-brain-pill"
            >
              {sbProvider && sbModel
                ? `Brain: ${sbProvider} · ${sbModel}`
                : sbProvider
                  ? `Brain: ${sbProvider}`
                  : 'Brain: open Switchboard →'}
            </button>
          )}
        </div>
        <div className="chat-header-actions chat-header-actions--primary">
          {token && (
            <button
              type="button"
              className="mode-toggle-btn switchboard-nav-btn chat-header-switchboard-primary"
              title={
                isOwnerAdmin
                  ? 'Vaulted Switchboard — API keys & brains (owner admin)'
                  : 'Vaulted Switchboard — your API keys & brains'
              }
              onClick={() => openChronicleSwitchboard(isOwnerAdmin)}
              data-testid="chat-open-switchboard-btn"
            >
              Switchboard
            </button>
          )}
          <button
            type="button"
            className="chronicle-theme-toggle-btn"
            title={uiTheme === 'light' ? 'Dark background' : 'Light background'}
            onClick={() => setUiTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            data-testid="chronicle-theme-toggle"
          >
            {uiTheme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          {webSearchAvailable && (
            <div className="web-search-header-cluster" data-testid="web-search-header-cluster">
              <button
                type="button"
                className={`mode-toggle-btn web-search-engine-btn ${webSearchProvider === 'brave' ? 'active' : ''}`}
                onClick={() => setWebSearchProvider('brave')}
                title="Web search via Brave (does not change chat brain)"
                data-testid="web-search-engine-brave"
              >
                BR
              </button>
              <button
                type="button"
                className={`mode-toggle-btn web-search-engine-btn ${webSearchProvider === 'serper' ? 'active' : ''}`}
                onClick={() => setWebSearchProvider('serper')}
                title="Web search via Serper (does not change chat brain)"
                data-testid="web-search-engine-serper"
              >
                SR
              </button>
              <button
                type="button"
                onClick={() => setWebSearchEnabled(!webSearchEnabled)}
                className={`mode-toggle-btn ${webSearchEnabled ? 'active' : ''}`}
                title={webSearchEnabled ? `Web search ON (${webSearchProvider})` : 'Web search OFF'}
                data-testid="web-search-toggle"
              >
                <Globe size={18} />
                Web
              </button>
            </div>
          )}
          <button
            onClick={async () => {
              if (!appBuilderMode && !SKIP_BILLING_GATE) {
                const latestCreditStatus = await fetchCreditStatus();
                if (latestCreditStatus && latestCreditStatus.credit_exhausted) {
                  toast.error(
                    formatCreditToastMessage('exhausted', latestCreditStatus, {
                      provider: sbProvider,
                      model: sbModel,
                      appBuilderMode,
                      builderTier,
                    })
                  );
                  return;
                }
                if (latestCreditStatus && latestCreditStatus.credit_low) {
                  toast.warn(
                    formatCreditToastMessage('low', latestCreditStatus, {
                      provider: sbProvider,
                      model: sbModel,
                      appBuilderMode,
                      builderTier,
                    })
                  );
                }
              }
              if (!appBuilderMode) {
                if (chatMode === 'ask' || chatMode === 'plan') setChatMode('agent');
                setShowBuilderTierPicker(true);
                return;
              }
              setAppBuilderMode(false);
              toast.success('Switched to Chat Mode');
            }}
            className={`mode-toggle-btn ${appBuilderMode ? 'active' : ''}`}
            title={appBuilderMode ? 'Click to switch to Chat Mode' : 'Click to switch to Builder Mode'}
            data-testid="app-builder-toggle-header"
          >
            <Code size={18} />
            {appBuilderMode ? 'Chat' : 'Builder'}
          </button>
          <div className="chat-header-actions--overflow" ref={headerMoreRef}>
            <button
              type="button"
              className="mode-toggle-btn"
              title="More tools"
              onClick={() => setHeaderMoreOpen((o) => !o)}
              data-testid="chronicle-header-more"
            >
              More
            </button>
            {headerMoreOpen ? (
              <div className="chat-header-more-panel">
          <button
            onClick={() =>
              setCompact((c) => {
                const next = !c;
                try { localStorage.setItem('chronicle_compact', next ? '1' : '0'); } catch {}
                try { window.dispatchEvent(new CustomEvent('chronicle:compact-change', { detail: next ? '1' : '0' })); } catch {}
                return next;
              })
            }
            className={`mode-toggle-btn ${compact ? 'active' : ''}`}
            title={compact ? 'Compact mode ON' : 'Compact mode OFF'}
            data-testid="compact-toggle"
          >
            Compact
          </button>
          <button
            onClick={async () => {
              if (!appBuilderMode && !SKIP_BILLING_GATE) {
                const latestCreditStatus = await fetchCreditStatus();
                if (latestCreditStatus && latestCreditStatus.credit_exhausted) {
                  toast.error(
                    formatCreditToastMessage('exhausted', latestCreditStatus, {
                      provider: sbProvider,
                      model: sbModel,
                      appBuilderMode,
                      builderTier,
                    })
                  );
                  return;
                }
                if (latestCreditStatus && latestCreditStatus.credit_low) {
                  toast.warn(
                    formatCreditToastMessage('low', latestCreditStatus, {
                      provider: sbProvider,
                      model: sbModel,
                      appBuilderMode,
                      builderTier,
                    })
                  );
                }
              }
              if (!appBuilderMode) {
                if (chatMode === 'ask' || chatMode === 'plan') setChatMode('agent');
                setShowBuilderTierPicker(true);
                return;
              }
              setAppBuilderMode(false);
              toast.success('Switched to Chat Mode');
            }}
            className={`mode-toggle-btn ${appBuilderMode ? 'active' : ''}`}
            title={appBuilderMode ? 'Click to switch to Chat Mode' : 'Click to switch to Builder Mode'}
            data-testid="app-builder-toggle"
          >
            <Code size={18} />
            {appBuilderMode ? 'Chat' : 'Builder'}
          </button>
          {appBuilderMode && (
            <select
              className="builder-tier-select"
              value={builderTier}
              onChange={(e) => {
                setBuilderTier(e.target.value);
                toast.success(`Builder tier set: ${e.target.value}`);
              }}
              title="Builder brain tier"
            >
              <option value="fast">Fast</option>
              <option value="balanced">Balanced</option>
              <option value="power">Power</option>
            </select>
          )}
          {appBuilderMode && (
            <button
              type="button"
              className="mode-toggle-btn"
              title="Host your built app on Chronicle (paid subscription)"
              onClick={() => setShowHostingModal(true)}
            >
              Host app
            </button>
          )}
          <BridgeUnlockControls sessionId={sessionId} />
          <button 
            onClick={toggleMemories} 
            className={`memory-toggle-btn ${showMemories ? 'active' : ''}`}
            title="View memories"
          >
            <Brain size={18} />
          </button>
          <div className="chat-switcher-wrapper">
            <button 
              onClick={() => { setShowChatSwitcher(!showChatSwitcher); fetchChatSessions(); }}
              className={`mode-toggle-btn ${showChatSwitcher ? 'active' : ''}`}
              title="Switch chats"
              data-testid="chat-switcher-btn"
            >
              <MessageSquare size={18} />
              Chats
            </button>
            {showChatSwitcher && (
              <div className="chat-switcher-dropdown">
                <div className="chat-switcher-header">
                  <span>Your Chats</span>
                  <button onClick={startNewChat} className="new-chat-small-btn">
                    <Plus size={14} /> New
                  </button>
                </div>
                {chatSessions.length === 0 ? (
                  <div className="no-chats">No previous chats</div>
                ) : (
                  chatSessions.slice(0, 10).map((session, idx) => (
                    <button
                      key={session._id || idx}
                      onClick={() => loadChatSession(session._id)}
                      className={`chat-session-item ${sessionId === session._id ? 'active' : ''}`}
                    >
                      <MessageSquare size={14} />
                      <span>{session.preview || `Chat ${idx + 1}`}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <button onClick={startNewChat} className="new-chat-btn" data-testid="new-chat-btn">
            <Plus size={18} />
            New
          </button>
          <button onClick={() => window.location.reload()} className="mode-toggle-btn" title="Refresh to sync">
            Sync
          </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showHostingModal && (
        <div className="builder-tier-modal">
          <div className="builder-tier-card hosting-checkout-card">
            <h3>Host your app on Chronicle</h3>
            <p className="section-desc" style={{ marginBottom: '1rem' }}>
              Separate from your main Chronicle chat plan. Choose billing — you&apos;ll confirm payment securely on Stripe.
              Stripe emails your receipt. Monthly <strong>£{Number(hostingPrices.monthly).toFixed(2)}</strong>/mo or yearly{' '}
              <strong>£{Number(hostingPrices.yearly).toFixed(2)}</strong>/yr (save ~20% vs 12× monthly).
            </p>
            <div className="builder-tier-options">
              <button
                type="button"
                disabled={hostingCheckoutLoading}
                onClick={() => startHostingCheckout('month')}
              >
                <strong>Pay monthly</strong>
                <span>£{Number(hostingPrices.monthly).toFixed(2)}/month</span>
              </button>
              <button
                type="button"
                disabled={hostingCheckoutLoading}
                onClick={() => startHostingCheckout('year')}
              >
                <strong>Pay yearly (better value)</strong>
                <span>£{Number(hostingPrices.yearly).toFixed(2)}/year</span>
              </button>
            </div>
            <button
              type="button"
              className="builder-tier-cancel"
              onClick={() => setShowHostingModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showBuilderTierPicker && (
        <div className="builder-tier-modal">
          <div className="builder-tier-card">
            <h3>Choose Builder Brain</h3>
            <p>Pick the right brain for the job and credit cost.</p>
            <div className="builder-tier-options">
              <button
                onClick={() => {
                  setBuilderTier('fast');
                  setAppBuilderMode(true);
                  setShowBuilderTierPicker(false);
                  toast.success('Builder Mode ON — Fast brain selected');
                }}
              >
                <strong>Fast (Cheapest)</strong>
                <span>Simple edits and quick tasks.</span>
              </button>
              <button
                onClick={() => {
                  setBuilderTier('balanced');
                  setAppBuilderMode(true);
                  setShowBuilderTierPicker(false);
                  toast.success('Builder Mode ON — Balanced brain selected');
                }}
              >
                <strong>Balanced</strong>
                <span>Regular app and plugin building.</span>
              </button>
              <button
                onClick={() => {
                  setBuilderTier('power');
                  setAppBuilderMode(true);
                  setShowBuilderTierPicker(false);
                  toast.success('Builder Mode ON — Power brain selected');
                }}
              >
                <strong>Power (Most Expensive)</strong>
                <span>Complex architecture and deep debugging.</span>
              </button>
            </div>
            <button className="builder-tier-cancel" onClick={() => setShowBuilderTierPicker(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Memories Panel */}
      {showMemories && (
        <div className="memories-panel">
          <div className="memories-panel-header">
            <h3>Memories</h3>
            <button onClick={() => setShowMemories(false)}><X size={18} /></button>
          </div>
          {loadingMemories ? (
            <div className="loading">Loading...</div>
          ) : memories.length === 0 ? (
            <div className="empty-memories">No memories yet</div>
          ) : (
            <div className="memories-list-panel">
              {memories.map(memory => (
                <div 
                  key={memory.id} 
                  className="memory-item-panel"
                  onClick={() => insertMemory(memory)}
                  title="Click to insert into chat"
                >
                  <span className="memory-key">{memory.key}</span>
                  <span className="memory-value">{memory.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {appBuilderMode ? (
        <div className="builder-workspace-bar" data-testid="builder-workspace-bar">
          <button
            type="button"
            className="builder-workspace-bar-btn"
            title="Open full preview view"
            onClick={() => openBuilderView()}
            data-testid="builder-pane-view"
          >
            <Eye size={16} />
            View
          </button>
          <button
            type="button"
            className="builder-workspace-bar-btn active"
            title="Chat / build"
            data-testid="builder-pane-code"
          >
            <Terminal size={16} />
            Code
          </button>
          <button
            type="button"
            className="builder-workspace-bar-btn"
            title="Build and preview your app"
            onClick={() => openBuilderView()}
            disabled={previewActionLoading}
            data-testid="builder-pane-preview"
          >
            <Eye size={16} />
            Preview
          </button>
          <button
            type="button"
            className="builder-workspace-bar-btn builder-workspace-bar-btn--deploy"
            onClick={() => setShowHostingModal(true)}
            data-testid="builder-pane-deploy"
          >
            <CloudUpload size={16} />
            Deploy
          </button>
        </div>
      ) : null}

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-chat chronicle-empty-minimal" aria-hidden="true" />
        ) : (
          messages.map((msg, idx) => {
            const uniqueKey = `${msg.role}-${idx}-${msg.content?.substring(0, 20) || ''}`;
            return (
            <div 
              key={uniqueKey} 
              className={`message ${msg.role}`}
              data-testid={`message-${msg.role}-${idx}`}
            >
              <div className="message-avatar">
                {msg.role === 'user' ? <User size={20} /> : <Brain size={20} />}
              </div>
              <div className="message-content">
                {msg.webSearch && msg.webSearch.query && !msg.webSearch.skipped ? (
                  <div className="web-search-results-panel" data-testid="web-search-results-panel">
                    <div className="web-search-results-header">
                      Chronicle searched ({msg.webSearch.provider === 'serper' ? 'Serper' : 'Brave'}): {msg.webSearch.query}
                    </div>
                    {msg.webSearch.empty || !(msg.webSearch.results && msg.webSearch.results.length) ? (
                      <p className="web-search-results-empty">No results returned from the search API.</p>
                    ) : (
                      <ol className="web-search-results-list">
                        {msg.webSearch.results.map((r, ri) => (
                          <li key={ri}>
                            <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title || r.url}</a>
                            {r.desc ? <p>{r.desc}</p> : null}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                ) : null}
                {msg.image && (
                  <img src={msg.image} alt="Uploaded" className="message-image" />
                )}
                {(msg.content || '').trim() ? parseMessage(msg.content).map((part, i) => (
                  part.type === 'code' ? (
                    <div key={i} className="code-block">
                      <div className="code-header">
                        <Code size={14} />
                        <span>{part.language}</span>
                        <button 
                          className="copy-code-btn"
                          onClick={() => copyToClipboard(part.content)}
                          title="Copy code"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <pre><code>{part.content}</code></pre>
                    </div>
                  ) : (
                    <pre key={i}>{part.content}</pre>
                  )
                )) : null}
                {msg.role === 'assistant' && (
                  <div className="message-actions">
                    <button 
                      className="copy-btn" 
                      onClick={() => copyToClipboard(msg.content)}
                      title="Copy message"
                    >
                      <Copy size={16} />
                    </button>
                    <button 
                      className="listen-btn" 
                      onClick={() => speakMessage(msg.content)}
                      title="Listen to response"
                    >
                      <Volume2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
          })
        )}
        {sending && (
          <div className="message assistant">
            <div className="message-avatar">
              <Brain size={20} />
            </div>
            <div className="message-content typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chronicle-composer-dock">
        <div className={`chronicle-composer-stack${hasImageDraft ? ' chronicle-composer-stack--has-image' : ''}`}>
          <div className="chronicle-builder-tabs" role="tablist" aria-label="Build type">
            {BUILDER_PRODUCT_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={builderProductTab === tab.id}
                className={`chronicle-builder-tab ${builderProductTab === tab.id ? 'active' : ''}`}
                onClick={() => selectBuilderProductTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

      <form
        onSubmit={sendMessage}
        className={`chat-input-form chronicle-composer-form${(hasImageDraft || input.trim()) ? ' chronicle-composer-has-draft' : ''}${hasImageDraft ? ' chronicle-composer-has-image' : ''}`}
      >
        {previewUrl && (
          <div className="chronicle-image-preview-row chronicle-image-preview-row--top">
            <img src={previewUrl} alt="paste preview" className="chronicle-image-preview-thumb" />
            <button type="button" onClick={clearFile} className="chronicle-image-preview-remove" title="Remove image">✕</button>
            <span className="chronicle-image-preview-hint">
              {input.trim() ? 'Optional note below — or send image now' : 'No typing needed — tap Send image'}
            </span>
            <button
              type="submit"
              className="chronicle-image-preview-send chronicle-image-preview-send--primary"
              disabled={sending}
              data-testid="send-image-button"
            >
              Send image
            </button>
          </div>
        )}

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,.pdf,.txt,.doc,.docx"
          style={{ display: 'none' }}
        />
        <input
          type="file"
          ref={cameraInputRef}
          onChange={handleCameraCapture}
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
        />

        <div className="chronicle-composer-actions chat-input-actions-row">
        <div className="chat-input-leading-cluster">
        {/* File Commands Dropdown */}
        <div className="file-commands-wrapper">
          <button 
            type="button" 
            className={`input-action-btn ${showFileCommands ? 'active' : ''}`}
            onClick={() => setShowFileCommands(!showFileCommands)}
            title="File commands"
          >
            <FolderOpen size={20} />
          </button>
          {showFileCommands && (
            <div className="file-commands-dropdown" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
              <button 
                type="button" 
                className="file-commands-close"
                onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowFileCommands(false); }}
                onClick={(e) => { e.stopPropagation(); setShowFileCommands(false); }}
              >
                <ChevronLeft size={18} />
              </button>
              {fileCommands.map((cmd, idx) => (
                <button
                  key={idx}
                  type="button"
                  onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleFileCommand(cmd); }}
                  onClick={(e) => { e.stopPropagation(); handleFileCommand(cmd); }}
                  className="file-command-item"
                >
                  {cmd.label}
                </button>
              ))}
            </div>
          )}
          
          {/* Files list dropdown */}
          {showFilesList && (
            <div className="file-commands-dropdown files-list-dropdown" onClick={(e) => e.stopPropagation()}>
              <button 
                type="button" 
                className="file-commands-close"
                onClick={(e) => { e.stopPropagation(); setShowFilesList(false); }}
              >
                <ChevronLeft size={18} />
              </button>
              <div className="files-list-header">My Files</div>
              {userFiles.length === 0 ? (
                <div className="file-command-item no-files">No files saved yet</div>
              ) : (
                userFiles.map((file, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleOpenFile(file.filename); }}
                    className="file-command-item file-link"
                  >
                    {file.filename}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        
        {/* Camera button */}
        <button 
          type="button" 
          className="input-action-btn"
          onClick={() => cameraInputRef.current?.click()}
          title="Take photo"
        >
          <Camera size={20} />
        </button>
        
        {/* File upload button */}
        <button 
          type="button" 
          className="input-action-btn"
          onClick={() => fileInputRef.current?.click()}
          title="Upload file"
        >
          <Paperclip size={20} />
        </button>
        </div>

        <p className="chronicle-build-tagline chronicle-build-tagline--in-row">Your next build starts here.</p>

        {user && builderProjects.length > 0 ? (
          <div className="chronicle-composer-actions-pill">
            <ChronicleProjectPicker
              user={user}
              projects={builderProjects}
              activeProjectId={activeProjectId}
              open={projectMenuOpen}
              onToggle={() => setProjectMenuOpen((o) => !o)}
              onSelect={selectBuilderProject}
              onCreate={createBuilderProject}
              menuRef={projectMenuRef}
            />
          </div>
        ) : null}

        <div className="chat-input-send-cluster">
        <VoiceInput
          onTranscription={(text) => setInput((prev) => (prev ? `${prev} ${text}` : text))}
          disabled={sending}
        />
        {sending ? (
          <button type="button" onClick={stopResponse} className="stop-btn" data-testid="stop-btn">
            <X size={20} />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!input.trim() && !hasImageDraft}
            className={hasImageDraft && !input.trim() ? 'send-btn-image-ready' : ''}
            title={hasImageDraft && !input.trim() ? 'Send image (no text needed)' : 'Send message'}
            data-testid="send-button"
          >
            <ArrowUp size={20} />
          </button>
        )}
        </div>
        </div>

        {/* Message field — full width row; Enter sends, Shift+Enter newline */}
        <textarea
          className="chat-message-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={(e) => {
            const items = e.clipboardData.items;
            for (let i = 0; i < items.length; i++) {
              if (items[i].type.startsWith('image/')) {
                e.preventDefault();
                const file = items[i].getAsFile();
                if (file) {
                  setSelectedFile(file);
                  setPreviewUrl(URL.createObjectURL(file));
                  toast.info('Image ready — press Enter or Send image');
                }
                return;
              }
            }
            const text = e.clipboardData.getData('text');
            if (text) {
              e.preventDefault();
              setInput(prev => prev + text);
            }
          }}
          placeholder={previewUrl ? 'Optional note about this image…' : 'Message Chronicle…'}
          disabled={false}
          data-testid="chat-input"
        />

        <div className={`agent-status ${sending ? 'working' : 'waiting'}`}>
          <span className="status-dot"></span>
          <span className="status-text">{sending ? 'Agent is working' : 'Agent is waiting'}</span>
        </div>

        <div className="chronicle-composer-toolbar chat-controls-toolbar" data-testid="chat-controls-row">
            <span className="chat-toolbar-label">Mode</span>
            <select
              id="chronicle-chat-mode"
              className="chat-mode-select"
              value={chatMode}
              aria-label="Mode: Agent, Plan, or Ask"
              onChange={(e) => {
                const v = e.target.value;
                setChatMode(v);
                persistChatPrefs(chatQuality, v);
              }}
              data-testid="chat-mode-select"
            >
              <option value="agent">Code</option>
              <option value="plan">Plan</option>
              <option value="ask">Ask</option>
            </select>
            <span className="chat-toolbar-label">Provider</span>
            <button
              type="button"
              className="chat-switchboard-brains-link"
              title="Open Switchboard to manage API keys and brains"
              onClick={() => openChronicleSwitchboard(isOwnerAdmin)}
              data-testid="chat-switchboard-brains-link"
            >
              Switchboard
            </button>
            <button title="Reload brains from Switchboard" onClick={loadSbBrains} style={{background:'transparent',border:'1px solid #2a2a3e',borderRadius:'6px',color:'#8b5cf6',cursor:'pointer',padding:'4px 8px',fontSize:'14px',lineHeight:1}}>↻</button>
            <select
              id="chronicle-chat-provider"
              className="chat-brain-select"
              value={sbProvider}
              aria-label="AI Provider"
              onChange={(e) => {
                setSbProvider(e.target.value);
                setSbModel('');
              }}
            >
              <option value="">— Provider —</option>
              {[...new Set(sbBrains.map(b => b.provider))].filter(
                (p) => !['serper', 'brave'].includes(String(p || '').toLowerCase())
              ).map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
              {sbBrains.length === 0 && <option value="auto">Auto (default)</option>}
            </select>
            <span className="chat-toolbar-label">Brain</span>
            <select
              id="chronicle-chat-brain"
              className={`chat-brain-select ${sbModel ? 'chat-brain-best' : ''}`}
              value={sbModel}
              aria-label="Brain / Model"
              onChange={(e) => setSbModel(e.target.value)}
            >
              <option value="">— Model —</option>
              {(() => {
                const fromApi = sbModelsByProvider[sbProvider];
                const modelList = Array.isArray(fromApi) && fromApi.length > 0
                  ? fromApi
                  : (sbBrains.filter((b) => b.provider === sbProvider).map((b) => b.model).filter(Boolean));
                // No hardcoded fallback - only show models from Switchboard brains
                const list = modelList.length > 0 ? modelList : [];
                return list.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ));
              })()}
              {sbBrains.length === 0 && <option value="auto">Auto</option>}
            </select>
            <span className="chat-toolbar-label">Voice</span>
            <select
              id="chronicle-chat-voice"
              className="chat-voice-select"
              value={chatVoice}
              aria-label="TTS Voice"
              onChange={(e) => setChatVoice(e.target.value)}
              data-testid="chat-voice-select"
            >
              <option value="">Default</option>
              {availableVoices.length === 0 && <option value="" disabled>No ElevenLabs voices — add key in Admin</option>}
              {availableVoices.map(v => (
                <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
              ))}
            </select>
        </div>
      </form>
        </div>
      </div>
    </div>
  );
};

// Contacts Component
const ContactsView = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '', notes: '' });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contacts`);
      setContacts(res.data);
    } catch (err) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const addContact = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/contacts`, newContact);
      setContacts(prev => [...prev, res.data]);
      setNewContact({ name: '', phone: '', email: '', notes: '' });
      setShowForm(false);
      toast.success('Contact added!');
    } catch (err) {
      toast.error('Failed to add contact');
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API_URL}/api/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Contact deleted');
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  return (
    <div className="contacts-view">
      <div className="view-header">
        <h2><Users size={24} /> Contacts</h2>
        <button onClick={() => setShowForm(!showForm)} className="add-btn" data-testid="add-contact-btn">
          <Plus size={18} />
          Add Contact
        </button>
      </div>

      {showForm && (
        <form onSubmit={addContact} className="contact-form">
          <input
            type="text"
            placeholder="Name"
            value={newContact.name}
            onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
            required
            data-testid="contact-name-input"
          />
          <input
            type="tel"
            placeholder="Phone"
            value={newContact.phone}
            onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
            required
            data-testid="contact-phone-input"
          />
          <input
            type="email"
            placeholder="Email (optional)"
            value={newContact.email}
            onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
            data-testid="contact-email-input"
          />
          <textarea
            placeholder="Notes (optional)"
            value={newContact.notes}
            onChange={(e) => setNewContact(prev => ({ ...prev, notes: e.target.value }))}
            data-testid="contact-notes-input"
          />
          <div className="form-buttons">
            <button type="button" onClick={() => setShowForm(false)} className="cancel-btn">Cancel</button>
            <button type="submit" data-testid="save-contact-btn">Save Contact</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="loading">Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <p>No contacts yet. Add your first contact!</p>
        </div>
      ) : (
        <div className="contacts-list">
          {contacts.map(contact => (
            <div key={contact.id} className="contact-card" data-testid={`contact-${contact.id}`}>
              <div className="contact-info">
                <h3>{contact.name}</h3>
                <p><Phone size={14} /> {contact.phone}</p>
                {contact.email && <p>{contact.email}</p>}
                {contact.notes && <p className="notes">{contact.notes}</p>}
              </div>
              <button 
                onClick={() => deleteContact(contact.id)} 
                className="delete-btn"
                data-testid={`delete-contact-${contact.id}`}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Memory View Component
const MemoryView = () => {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemories();
  }, []);

  const fetchMemories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/memory`);
      setMemories(res.data);
    } catch (err) {
      toast.error('Failed to load memories');
    } finally {
      setLoading(false);
    }
  };

  const deleteMemory = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/memory/${id}`);
      setMemories(prev => prev.filter(m => m.id !== id));
      toast.success('Memory deleted');
    } catch (err) {
      toast.error('Failed to delete memory');
    }
  };

  return (
    <div className="memory-view">
      <div className="view-header">
        <h2><Brain size={24} /> What I Remember</h2>
      </div>

      {loading ? (
        <div className="loading">Loading memories...</div>
      ) : memories.length === 0 ? (
        <div className="empty-state">
          <Brain size={48} />
          <p>I don't have any stored memories yet.</p>
          <p>As we chat, I'll remember important things you tell me!</p>
        </div>
      ) : (
        <div className="memories-list">
          {memories.map(memory => (
            <div key={memory.id} className="memory-card" data-testid={`memory-${memory.id}`}>
              <div className="memory-content">
                <span className="memory-category">{memory.category}</span>
                <p><strong>{memory.key}</strong></p>
                <p>{memory.value}</p>
                <span className="memory-date">{new Date(memory.created_at).toLocaleDateString()}</span>
              </div>
              <button 
                onClick={() => deleteMemory(memory.id)} 
                className="delete-btn"
                data-testid={`delete-memory-${memory.id}`}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Phone & SMS View
const PhoneView = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('send');
  const [contacts, setContacts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [smsHistory, setSmsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isAdminOrPartner, setIsAdminOrPartner] = useState(false);
  
  // SMS form
  const [smsPhone, setSmsPhone] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [smsContactName, setSmsContactName] = useState('');
  
  // Call form
  const [callPhone, setCallPhone] = useState('');
  const [callMessage, setCallMessage] = useState('');
  const [callContactName, setCallContactName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = readStoredToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [contactsRes, subRes, callsRes, smsRes, adminRes] = await Promise.all([
        axios.get(`${API_URL}/api/contacts`, { headers }),
        axios.get(`${API_URL}/api/subscription/status`, { headers }),
        axios.get(`${API_URL}/api/call/history`, { headers }).catch(() => ({ data: { calls: [] } })),
        axios.get(`${API_URL}/api/sms/history`, { headers }).catch(() => ({ data: { messages: [] } })),
        axios.get(`${API_URL}/api/user/is-admin`, { headers }).catch(() => ({ data: { is_admin: false } }))
      ]);
      setContacts(contactsRes.data || []);
      setSubscription(subRes.data || {});
      setCallHistory(callsRes.data?.calls || []);
      setSmsHistory(smsRes.data?.messages || []);
      setIsAdminOrPartner(adminRes.data?.is_admin || adminRes.data?.is_partner || false);
    } catch (err) {
      console.error('Failed to load phone data:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectContact = (contact, type) => {
    if (type === 'sms') {
      setSmsPhone(contact.phone);
      setSmsContactName(contact.name);
    } else {
      setCallPhone(contact.phone);
      setCallContactName(contact.name);
    }
  };

  const sendSMS = async (e) => {
    e.preventDefault();
    if (!smsPhone || !smsMessage) return;
    
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/sms/send`, {
        to_phone: smsPhone,
        message: smsMessage,
        contact_name: smsContactName || null
      });
      
      if (res.data.success) {
        toast.success('SMS sent successfully!');
        setSmsMessage('');
        fetchData(); // Refresh history
      } else {
        toast.error(res.data.error || 'Failed to send SMS');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const makeCall = async (e) => {
    e.preventDefault();
    if (!callPhone || !callMessage) return;
    
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/call/make`, {
        to_phone: callPhone,
        message: callMessage,
        contact_name: callContactName || null
      });
      
      if (res.data.success) {
        toast.success('Call initiated!');
        setCallMessage('');
        fetchData(); // Refresh history
      } else {
        toast.error(stripAnsi(res.data.error) || 'Failed to make call');
      }
    } catch (err) {
      toast.error(stripAnsi(err.response?.data?.detail) || 'Failed to make call');
    } finally {
      setSending(false);
    }
  };

  // Admin/partner can always use phone features
  const canUsePhoneFeatures = isAdminOrPartner || (subscription?.plan && !subscription?.plan.includes('starter'));

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="phone-view">
      <div className="view-header">
        <h2><Phone size={24} /> Phone & SMS</h2>
        {subscription?.is_subscribed && (
          <div className="usage-badges">
            <span className="usage-badge">
              <Phone size={14} /> {subscription.call_minutes_remaining} mins
            </span>
            <span className="usage-badge">
              <MessageSquare size={14} /> {subscription.texts_remaining} texts
            </span>
          </div>
        )}
      </div>

      {!canUsePhoneFeatures ? (
        <div className="upgrade-prompt">
          <Phone size={48} />
          <h3>{user?.is_subscribed ? 'Upgrade to Pro or Business' : 'Phone & SMS Available After Trial'}</h3>
          <p>{user?.is_subscribed 
            ? 'Phone calls and SMS require a Pro or Business subscription.' 
            : 'Complete your 10-day trial to unlock Phone & SMS features.'}</p>
          <p>Pro includes 60 minutes and 100 texts per month.</p>
        </div>
      ) : (
        <>
          <div className="phone-tabs">
            <button 
              className={activeTab === 'send' ? 'active' : ''}
              onClick={() => setActiveTab('send')}
              data-testid="tab-send"
            >
              Send Message
            </button>
            <button 
              className={activeTab === 'call' ? 'active' : ''}
              onClick={() => setActiveTab('call')}
              data-testid="tab-call"
            >
              Make Call
            </button>
            <button 
              className={activeTab === 'history' ? 'active' : ''}
              onClick={() => setActiveTab('history')}
              data-testid="tab-history"
            >
              History
            </button>
          </div>

          {activeTab === 'send' && (
            <div className="phone-section">
              <div className="contacts-quick-select">
                <h4>Quick Select Contact</h4>
                <div className="contact-chips">
                  {contacts.slice(0, 5).map(contact => (
                    <button 
                      key={contact.id}
                      onClick={() => selectContact(contact, 'sms')}
                      className={smsPhone === contact.phone ? 'selected' : ''}
                    >
                      {contact.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <form onSubmit={sendSMS} className="phone-form">
                <input
                  type="tel"
                  placeholder="Phone number (e.g., +44...)"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  required
                  data-testid="sms-phone-input"
                />
                <textarea
                  placeholder="Your message..."
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  required
                  rows={3}
                  data-testid="sms-message-input"
                />
                <button type="submit" disabled={sending} data-testid="send-sms-btn">
                  {sending ? 'Sending...' : 'Send SMS'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'call' && (
            <div className="phone-section">
              <div className="contacts-quick-select">
                <h4>Quick Select Contact</h4>
                <div className="contact-chips">
                  {contacts.slice(0, 5).map(contact => (
                    <button 
                      key={contact.id}
                      onClick={() => selectContact(contact, 'call')}
                      className={callPhone === contact.phone ? 'selected' : ''}
                    >
                      {contact.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <form onSubmit={makeCall} className="phone-form">
                <input
                  type="tel"
                  placeholder="Phone number (e.g., +44...)"
                  value={callPhone}
                  onChange={(e) => setCallPhone(e.target.value)}
                  required
                  data-testid="call-phone-input"
                />
                <textarea
                  placeholder="What should the AI say? (e.g., 'Hi John, this is a reminder about your appointment at 3pm today.')"
                  value={callMessage}
                  onChange={(e) => setCallMessage(e.target.value)}
                  required
                  rows={3}
                  data-testid="call-message-input"
                />
                <button type="submit" disabled={sending} data-testid="make-call-btn">
                  {sending ? 'Calling...' : 'Make Call'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="phone-section history-section">
              <h4>SMS History</h4>
              {smsHistory.length === 0 ? (
                <p className="no-history">No messages sent yet.</p>
              ) : (
                <div className="history-list">
                  {smsHistory.slice(0, 10).map(msg => (
                    <div key={msg.id} className="history-item">
                      <span className="history-icon"><MessageSquare size={16} /></span>
                      <div className="history-details">
                        <strong>{msg.contact_name || msg.to_phone}</strong>
                        <p>{msg.message}</p>
                        <span className="history-date">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <span className={`status-badge ${msg.status}`}>{msg.status}</span>
                    </div>
                  ))}
                </div>
              )}

              <h4>Call History</h4>
              {callHistory.length === 0 ? (
                <p className="no-history">No calls made yet.</p>
              ) : (
                <div className="history-list">
                  {callHistory.slice(0, 10).map(call => (
                    <div key={call.id} className="history-item">
                      <span className="history-icon"><Phone size={16} /></span>
                      <div className="history-details">
                        <strong>{call.contact_name || call.to_phone}</strong>
                        <p>{call.message?.substring(0, 50)}...</p>
                        <span className="history-date">
                          {new Date(call.created_at).toLocaleString()}
                          {call.duration && ` • ${call.duration}s`}
                        </span>
                      </div>
                      <span className={`status-badge ${call.status}`}>{call.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Car Mode View
const CarModeView = ({ onExit }) => {
  const voiceInputRef = useRef(null);
  const continuousListeningRef = useRef(true);
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [handsFree, setHandsFree] = useState(true);
  const [continuousListening, setContinuousListening] = useState(true);
  const [voiceLoopArmed, setVoiceLoopArmed] = useState(false);
  const [lastUserText, setLastUserText] = useState('');
  const [lastReply, setLastReply] = useState(
    'Tap the mic once — after that, listening restarts automatically between replies (toggle below to turn off).'
  );
  const [callPhone, setCallPhone] = useState('');
  const [carConnectionMode, setCarConnectionMode] = useState('bluetooth');

  continuousListeningRef.current = continuousListening;

  const scheduleNextListen = useCallback(() => {
    if (!voiceLoopArmed || !continuousListeningRef.current) return;
    window.setTimeout(() => {
      voiceInputRef.current?.startListening?.();
    }, 450);
  }, [voiceLoopArmed]);

  const speak = useCallback((text, onAfterSpeak) => {
    if (!('speechSynthesis' in window)) {
      onAfterSpeak?.();
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-GB';
      utterance.rate = 1;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        setIsSpeaking(false);
        onAfterSpeak?.();
      };
      utterance.onerror = () => {
        setIsSpeaking(false);
        onAfterSpeak?.();
      };
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      setIsSpeaking(false);
      onAfterSpeak?.();
    }
  }, []);

  const sendCarMessage = async (messageText) => {
    const text = (messageText || '').trim();
    if (!text || sending) return;
    setSending(true);
    setLastUserText(text);
    try {
      const token = readStoredToken();
      const res = await axios.post(`${API_URL}/api/chat`, {
        message: text,
        session_id: sessionId,
        app_builder_mode: false,
        web_search: false,
        car_mode: true,
        provider: sbProvider || undefined,
        model: sbModel || undefined
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!sessionId && res.data?.session_id) {
        setSessionId(res.data.session_id);
      }
      const reply = res.data?.response || 'Done.';
      setLastReply(reply);
      if (handsFree) {
        speak(reply, () => scheduleNextListen());
      } else {
        scheduleNextListen();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setLastReply(detail);
      } else if (detail?.warning_message) {
        setLastReply(detail.warning_message);
      } else {
        setLastReply('I could not process that. Please try again.');
      }
      scheduleNextListen();
    } finally {
      setSending(false);
    }
  };

  const handleNativeCall = async () => {
    if (!callPhone.trim()) {
      toast.error('Enter phone number');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/call/place`, {
        to: callPhone.trim(),
        use_native: true
      });
      if (res.data?.native && res.data?.link) {
        window.location.href = res.data.link;
      } else {
        toast.error('Could not open dialer');
      }
    } catch (err) {
      toast.error('Failed to open phone dialer');
    }
  };

  const brainStateClass = sending ? 'sending' : isSpeaking ? 'speaking' : isListening ? 'listening' : 'idle';

  return (
    <div className="car-mode-view">
      <div className="car-mode-safety-banner">
        Safety first: use voice + hands-free only while driving. Pull over for typing.
      </div>

      <div className="car-mode-header">
        <h2><Car size={24} /> Car Mode</h2>
        <div className="car-mode-header-actions">
          <label className="car-mode-toggle">
            <input
              type="checkbox"
              checked={handsFree}
              onChange={(e) => setHandsFree(e.target.checked)}
            />
            Hands-free voice reply
          </label>
          <label className="car-mode-toggle">
            <input
              type="checkbox"
              checked={continuousListening}
              onChange={(e) => setContinuousListening(e.target.checked)}
            />
            Auto-restart mic (tap mic once to start)
          </label>
          <button
            type="button"
            className="car-mode-exit-btn"
            onClick={() => onExit && onExit()}
          >
            Exit Car Mode
          </button>
        </div>
      </div>

      <div className="car-connection-box">
        <h3>Car Connection</h3>
        <p>Select how Chronicle should run through your car audio.</p>
        <div className="car-connection-modes">
          <button
            type="button"
            className={`car-btn-bluetooth${carConnectionMode === 'bluetooth' ? ' active' : ''}`}
            onClick={() => setCarConnectionMode('bluetooth')}
          >
            Bluetooth
          </button>
          <button
            type="button"
            className={`car-btn-android${carConnectionMode === 'android_auto' ? ' active' : ''}`}
            onClick={() => setCarConnectionMode('android_auto')}
          >
            Android Auto
          </button>
        </div>
        <div className="car-connection-help">
          {carConnectionMode === 'bluetooth' ? (
            <p>Pair your phone with your car Bluetooth and choose the car as audio output for calls and media.</p>
          ) : (
            <p>Open Android Auto on your car display (USB or wireless), then keep Chronicle in hands-free mode.</p>
          )}
        </div>
      </div>

      <div className={`car-brain-wrap ${brainStateClass}`}>
        <div className="car-brain-pulse" />
        <Brain size={64} className="car-brain-icon" />
      </div>

      <div className="car-mode-status">
        {sending ? 'Thinking...' : isSpeaking ? 'Speaking...' : isListening ? 'Listening...' : 'Ready'}
      </div>

      <div className="car-mode-voice-controls">
        <div className="car-conversation-label">Chronicle Conversation</div>
        <VoiceInput
          ref={voiceInputRef}
          onTranscription={(text) => sendCarMessage(text)}
          disabled={sending}
          onRecordingChange={(active) => {
            setIsListening(active);
            if (active) setVoiceLoopArmed(true);
          }}
          speechRecognitionFallback
          suppressFallbackInfoToast
          deviceOnly
        />
        <button
          type="button"
          className="car-stop-speech-btn"
          onClick={() => {
            window.speechSynthesis?.cancel();
            setIsSpeaking(false);
          }}
        >
          Stop Voice
        </button>
      </div>

      <div className="car-mode-last">
        <p><strong>You:</strong> {lastUserText || '—'}</p>
        <p><strong>Chronicle:</strong> {lastReply}</p>
      </div>

      <div className="car-mode-call-box">
        <h3>Quick Call</h3>
        <input
          type="tel"
          value={callPhone}
          onChange={(e) => setCallPhone(e.target.value)}
          placeholder="Phone number (e.g., +44...)"
        />
        <button type="button" onClick={handleNativeCall}>
          Call via My Phone (No AI / No Credits)
        </button>
      </div>

      <div className="car-mode-footer-actions">
        <button
          type="button"
          className="car-mode-exit-btn secondary"
          onClick={() => onExit && onExit()}
        >
          Back to Normal Mode
        </button>
      </div>
    </div>
  );
};

// Subscription/Pricing View
const SubscriptionView = () => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { user } = useAuth();

  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (!user?.trial_ends_at) return 0;
    const trialEnd = new Date(user.trial_ends_at);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/plans`),
        axios.get(`${API_URL}/api/subscription/status`)
      ]);
      setPlans(plansRes.data.plans);
      setSubscription(subRes.data);
    } catch (err) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (SKIP_BILLING_GATE) {
      toast.message('Local dev: billing is off — no Stripe checkout.');
      return;
    }
    setProcessingPlan(planId);
    try {
      const res = await axios.post(`${API_URL}/api/trial/setup`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  const getFilteredPlans = () => {
    return plans.filter(plan => 
      billingCycle === 'monthly' ? plan.interval === 'month' : plan.interval === 'year'
    );
  };

  const getPlanIcon = (planId) => {
    if (planId.includes('business')) return <Crown size={24} />;
    if (planId.includes('pro')) return <Zap size={24} />;
    return <Brain size={24} />;
  };

  if (loading) {
    return <div className="loading">Loading plans...</div>;
  }

  return (
    <div className="subscription-view">
      <div className="subscription-header">
        <h2><CreditCard size={24} /> Subscription</h2>
        {subscription?.is_subscribed && (
          <div className="current-plan-badge">
            <CheckCircle size={16} />
            {subscription.plan_name} Plan Active
          </div>
        )}
      </div>

      {/* Trial Status Card */}
      {user?.trial_active && !user?.is_subscribed && (
        <div className="trial-status-card" data-testid="trial-status-card">
          <div className="trial-status-header">
            <Zap size={24} />
            <h3>Free Trial Active</h3>
          </div>
          <p className="trial-days-left">
            <strong>{trialDaysRemaining}</strong> day{trialDaysRemaining !== 1 ? 's' : ''} remaining
          </p>
          <p className="trial-info">
            Subscribe now to keep your memories and continue using Chronicle after your trial ends.
          </p>
        </div>
      )}

      {/* Trial Expired Card */}
      {!user?.trial_active && !user?.is_subscribed && (
        <div className="trial-expired-card" data-testid="trial-expired-card">
          <div className="trial-status-header">
            <Shield size={24} />
            <h3>Trial Ended</h3>
          </div>
          <p className="trial-info">
            Your free trial has ended, but don't worry - all your memories and data are preserved. 
            Subscribe to a plan below to continue using Chronicle.
          </p>
        </div>
      )}

      {subscription?.is_subscribed && (
        <div className="subscription-status-card">
          <h3>Your Current Plan: {subscription.plan_name}</h3>
          <div className="usage-stats">
            <div className="stat">
              <Phone size={18} />
              <span>{subscription.call_minutes_remaining} minutes remaining</span>
            </div>
            <div className="stat">
              <MessageSquare size={18} />
              <span>{subscription.texts_remaining} texts remaining</span>
            </div>
          </div>
          <p className="renewal-date">
            Renews: {new Date(subscription.subscription_ends_at).toLocaleDateString()}
          </p>
        </div>
      )}

      <div className="billing-toggle">
        <button 
          className={billingCycle === 'monthly' ? 'active' : ''}
          onClick={() => setBillingCycle('monthly')}
          data-testid="billing-monthly"
        >
          Monthly
        </button>
        <button 
          className={billingCycle === 'annual' ? 'active' : ''}
          onClick={() => setBillingCycle('annual')}
          data-testid="billing-annual"
        >
          Annual <span className="save-badge">Save 17%</span>
        </button>
      </div>

      <div className="plans-grid">
        {getFilteredPlans().map(plan => (
          <div 
            key={plan.id} 
            className={`plan-card ${plan.id.includes('pro') ? 'popular' : ''}`}
            data-testid={`plan-${plan.id}`}
          >
            {plan.id.includes('pro') && <div className="popular-badge">Most Popular</div>}
            
            <div className="plan-icon">
              {getPlanIcon(plan.id)}
            </div>
            
            <h3>{plan.name.replace(' (Annual)', '')}</h3>
            
            <div className="plan-price">
              <span className="currency">£</span>
              <span className="amount">{plan.price.toFixed(0)}</span>
              <span className="period">/{plan.interval === 'year' ? 'year' : 'mo'}</span>
            </div>

            <ul className="plan-features">
              {plan.features.map((feature, idx) => (
                <li key={idx}>
                  <Check size={16} />
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSubscribe(plan.id)}
              disabled={processingPlan === plan.id || (subscription?.plan === plan.id)}
              className="subscribe-btn"
              data-testid={`subscribe-${plan.id}`}
            >
              {processingPlan === plan.id ? 'Processing...' : 
               subscription?.plan === plan.id ? 'Current Plan' : 'Subscribe'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Subscription Success Page
const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const token = readStoredToken();
      const res = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful! Welcome to Chronicle Pro!');
        setTimeout(() => navigate('/'), 3000);
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="subscription-success">
      <div className="success-container">
        {status === 'checking' && (
          <>
            <Brain size={64} className="spin" />
            <h2>Processing your payment...</h2>
            <p>Please wait while we confirm your subscription.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle size={64} className="success-icon" />
            <h2>Payment Successful!</h2>
            <p>Welcome to Chronicle! Redirecting you to your dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <X size={64} className="error-icon" />
            <h2>Something went wrong</h2>
            <p>Please contact support if you were charged.</p>
            <button onClick={() => navigate('/')} className="back-btn">
              Go to Dashboard
            </button>
          </>
        )}
        
        {status === 'timeout' && (
          <>
            <Brain size={64} />
            <h2>Taking longer than expected</h2>
            <p>Your payment may still be processing. Check your email for confirmation.</p>
            <button onClick={() => navigate('/')} className="back-btn">
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/** After Stripe returns from app-hosting checkout */
const HostingSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 6) {
      setStatus('timeout');
      return;
    }
    try {
      const token = readStoredToken();
      const res = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('App hosting subscription active');
        setTimeout(() => navigate('/'), 3000);
      } else {
        setAttempts((a) => a + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="subscription-success">
      <div className="success-container">
        {status === 'checking' && (
          <>
            <Brain size={64} className="spin" />
            <h2>Confirming hosting…</h2>
            <p>Almost done. Stripe may also email your receipt.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={64} className="success-icon" />
            <h2>Hosting billing set up</h2>
            <p>We&apos;ll follow up with deployment steps for your app. Back to Chronicle…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <X size={64} className="error-icon" />
            <h2>Could not confirm yet</h2>
            <p>If you were charged, keep your Stripe email receipt and contact support.</p>
            <button onClick={() => navigate('/')} className="back-btn">
              Dashboard
            </button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <Brain size={64} />
            <h2>Still processing</h2>
            <p>Check your email from Stripe. You can return to Chronicle in a moment.</p>
            <button onClick={() => navigate('/')} className="back-btn">
              Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Voice Clone Section Component
const VOICE_CLONE_COST = 75;

const VoiceCloneSection = ({ onVoiceCloned, userCredits, onCreditsNeeded }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [sampleText, setSampleText] = useState('');
  const [instructions, setInstructions] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const hasEnoughCredits = userCredits >= VOICE_CLONE_COST;

  useEffect(() => {
    fetchSampleText();
  }, []);

  const fetchSampleText = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/voice/clone/sample-text`);
      setSampleText(res.data.text);
      setInstructions(res.data.instructions);
    } catch (err) {
      console.error('Failed to fetch sample text');
    }
  };

  const handleStartClone = () => {
    if (!hasEnoughCredits) {
      toast.error(`You need ${VOICE_CLONE_COST} credits to clone your voice. You have ${userCredits}.`);
      if (onCreditsNeeded) onCreditsNeeded();
      return;
    }
    setShowConfirm(true);
  };

  const confirmAndRecord = () => {
    setShowConfirm(false);
    startRecording();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await uploadVoiceSample(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const uploadVoiceSample = async (audioBlob) => {
    setIsCloning(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice_sample.webm');

      const token = readStoredToken();
      const res = await axios.post(`${API_URL}/api/voice/clone`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(res.data.message || 'Voice cloned successfully!');
      if (onVoiceCloned) onVoiceCloned();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clone voice');
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <div className="settings-section voice-clone-section">
      <h3><Mic size={20} /> Clone Your Voice</h3>
      <p className="section-desc">Record your voice to create a personalized AI voice clone</p>
      <p className="clone-cost-notice">Cost: {VOICE_CLONE_COST} credits (You have: {userCredits})</p>
      
      {sampleText && (
        <div className="sample-text-box">
          <p className="sample-instructions">{instructions}</p>
          <p className="sample-text">"{sampleText}"</p>
        </div>
      )}

      {showConfirm && (
        <div className="credit-confirm-modal">
          <div className="credit-confirm-content">
            <h4>Confirm Voice Clone</h4>
            <p>This will deduct <strong>{VOICE_CLONE_COST} credits</strong> from your balance.</p>
            <p>Your balance: {userCredits} credits</p>
            <p>After: {userCredits - VOICE_CLONE_COST} credits</p>
            <div className="credit-confirm-buttons">
              <button onClick={() => setShowConfirm(false)} className="cancel-btn">Cancel</button>
              <button onClick={confirmAndRecord} className="confirm-btn">Confirm & Record</button>
            </div>
          </div>
        </div>
      )}

      <div className="voice-clone-controls">
        {isCloning ? (
          <div className="cloning-status">
            <div className="spinner"></div>
            <span>Creating your voice clone...</span>
          </div>
        ) : isRecording ? (
          <button onClick={stopRecording} className="record-btn recording" data-testid="stop-recording-btn">
            <div className="recording-indicator"></div>
            Stop Recording
          </button>
        ) : (
          <button 
            onClick={handleStartClone} 
            className={`record-btn ${!hasEnoughCredits ? 'disabled' : ''}`}
            data-testid="start-recording-btn"
          >
            <Mic size={20} />
            {hasEnoughCredits ? 'Start Recording' : `Need ${VOICE_CLONE_COST} Credits`}
          </button>
        )}
      </div>
    </div>
  );
};

// User Settings View
const UserSettingsView = () => {
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    rules: [],
    custom_sms_templates: [],
    timezone: '',
    language: 'en',
    notification_email: true,
    notification_sms: true,
    custom_greeting: '',
    selected_voice: ''
  });
  const [credits, setCredits] = useState(0);
  const [newRule, setNewRule] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [homeAssistantUrl, setHomeAssistantUrl] = useState('');
  const [homeAssistantToken, setHomeAssistantToken] = useState('');
  const [haConnected, setHaConnected] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchVoices();
  }, []);

  const fetchVoices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/voices`);
      setVoices(res.data.voices || []);
    } catch (err) {
      console.error('Failed to load voices');
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/user/settings`);
      setSettings(res.data.settings || {});
      setCredits(res.data.credits || 0);
      setHaConnected(res.data.settings?.home_assistant_connected || false);
    } catch (err) {
      console.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/user/settings`, settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addRule = async () => {
    if (newRule.trim()) {
      const updatedRules = [...(settings.rules || []), newRule.trim()];
      setSettings({ ...settings, rules: updatedRules });
      setNewRule('');
      // Auto-save rules
      try {
        await axios.post(`${API_URL}/api/user/settings/rules`, { rules: updatedRules });
        toast.success('Rule added');
      } catch (err) {
        toast.error('Failed to save rule');
      }
    }
  };

  const removeRule = async (index) => {
    const updatedRules = settings.rules.filter((_, i) => i !== index);
    setSettings({ ...settings, rules: updatedRules });
    // Auto-save rules
    try {
      await axios.post(`${API_URL}/api/user/settings/rules`, { rules: updatedRules });
      toast.success('Rule removed');
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const connectHomeAssistant = async () => {
    if (!homeAssistantUrl || !homeAssistantToken) {
      toast.error('Please enter URL and token');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/user/settings/home-assistant`, {
        url: homeAssistantUrl,
        access_token: homeAssistantToken
      });
      toast.success('Home Assistant connected');
      setHaConnected(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect');
    }
  };

  const exportData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/user/data/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chronicle-data.json';
      a.click();
      toast.success('Data exported');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="settings-view" data-testid="settings-view">
      <div className="view-header">
        <h2><Settings size={24} /> Settings</h2>
      </div>

      <div className="settings-section">
        <h3>Credits</h3>
        <div className="credits-display">
          <span className="credits-amount">{credits}</span>
          <span className="credits-label">credits remaining</span>
        </div>
      </div>

      <div className="settings-section">
        <h3>AI Rules</h3>
        <p className="section-desc">Set rules for how Chronicle responds to you</p>
        <div className="rules-list">
          {(settings.rules || []).map((rule, index) => (
            <div key={index} className="rule-item">
              <span>{index + 1}. {rule}</span>
              <button onClick={() => removeRule(index)} className="remove-btn">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="add-rule">
          <input
            type="text"
            value={newRule}
            onChange={(e) => setNewRule(e.target.value)}
            placeholder="e.g., Keep replies short unless told otherwise"
            onKeyDown={(e) => e.key === 'Enter' && addRule()}
          />
          <button onClick={addRule} className="add-btn">
            <Plus size={20} />
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h3>Custom Greeting</h3>
        <input
          type="text"
          value={settings.custom_greeting || ''}
          onChange={(e) => setSettings({ ...settings, custom_greeting: e.target.value })}
          placeholder="e.g., Hey boss!"
        />
      </div>

      <div className="settings-section">
        <h3>Notifications</h3>
        <label className="toggle-setting">
          <input
            type="checkbox"
            checked={settings.notification_email}
            onChange={(e) => setSettings({ ...settings, notification_email: e.target.checked })}
          />
          Email notifications
        </label>
        <label className="toggle-setting">
          <input
            type="checkbox"
            checked={settings.notification_sms}
            onChange={(e) => setSettings({ ...settings, notification_sms: e.target.checked })}
          />
          SMS notifications
        </label>
      </div>

      <div className="settings-section">
        <h3>Voice for Calls</h3>
        <p className="section-desc">Select a voice for when Chronicle makes calls on your behalf</p>
        <select
          value={settings.selected_voice || ''}
          onChange={(e) => setSettings({ ...settings, selected_voice: e.target.value })}
          className="voice-select"
        >
          <option value="">Default voice</option>
          {voices.map((voice) => (
            <option key={voice.id} value={voice.id}>{voice.name}</option>
          ))}
        </select>
      </div>

      <VoiceCloneSection 
        onVoiceCloned={fetchVoices} 
        userCredits={credits}
        onCreditsNeeded={() => toast.info('Go to Subscription to add more credits')}
      />

      <div className="settings-section">
        <h3>Smart Home (Home Assistant)</h3>
        {haConnected ? (
          <div className="ha-connected">
            <CheckCircle size={20} /> Connected
          </div>
        ) : (
          <div className="ha-connect">
            <input
              type="text"
              value={homeAssistantUrl}
              onChange={(e) => setHomeAssistantUrl(e.target.value)}
              placeholder="http://homeassistant.local:8123"
            />
            <input
              type="password"
              value={homeAssistantToken}
              onChange={(e) => setHomeAssistantToken(e.target.value)}
              placeholder="Long-lived access token"
            />
            <button onClick={connectHomeAssistant}>Connect</button>
          </div>
        )}
      </div>

      <div className="settings-section">
        <h3>Data</h3>
        <button onClick={exportData} className="export-btn">
          <Download size={20} /> Export My Data
        </button>
        <button onClick={async () => {
          if (window.confirm('Delete all your chat history? This cannot be undone.')) {
            try {
              await axios.delete(`${API_URL}/api/user/chats`);
              toast.success('Chat history deleted');
            } catch (err) {
              toast.error('Failed to delete chats');
            }
          }
        }} className="delete-data-btn">
          <Trash2 size={20} /> Delete All Chats
        </button>
        <button onClick={async () => {
          if (window.confirm('Delete all your memories? Chronicle will forget everything about you. This cannot be undone.')) {
            try {
              await axios.delete(`${API_URL}/api/user/memories`);
              toast.success('Memories deleted');
            } catch (err) {
              toast.error('Failed to delete memories');
            }
          }
        }} className="delete-data-btn">
          <Trash2 size={20} /> Delete All Memories
        </button>
      </div>

      <div className="settings-actions">
        <button onClick={saveSettings} disabled={saving} className="save-btn">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

// Owner Admin View
const OwnerAdminView = () => {
  const ADMIN_SECTIONS = {
    OVERVIEW: 'overview',
    USERS: 'users',
    PARTNERS: 'partners',
    COMMS: 'communications',
    USAGE: 'usage',
    SECRETS: 'secrets',
    BRIDGE_PATHS: 'bridge-paths'
  };
  const [stats, setStats] = useState({ total_users: 0, active_users: 0, total_conversations: 0 });
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPartnerEmail, setNewPartnerEmail] = useState('');
  const [creditAmount, setCreditAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [showPhonePanel, setShowPhonePanel] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [selectedUserUsage, setSelectedUserUsage] = useState(null);
  const [creditWarningThreshold, setCreditWarningThreshold] = useState(20);
  const [creditWarningMessage, setCreditWarningMessage] = useState('Your credits are running low. Please top up to continue.');
  const [featurePricing, setFeaturePricing] = useState({
    app_builder_fast: 2,
    app_builder_balanced: 4,
    app_builder_power: 7,
    voice_clone: 75,
    voice_tts: 1,
    sms_send: 1,
    call_make: 2,
    call_with_voice: 3,
    car_mode_conversation: 1
  });
  const [activeAdminSection, setActiveAdminSection] = useState(ADMIN_SECTIONS.OVERVIEW);
  const [isOwner, setIsOwner] = useState(false);
  const [secretsStatus, setSecretsStatus] = useState(null);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [bridgeHostStatus, setBridgeHostStatus] = useState(null);
  const [safeEditGuards, setSafeEditGuards] = useState(null);
  const [bridgePathsLines, setBridgePathsLines] = useState('');
  const [bridgePathsEnv, setBridgePathsEnv] = useState([]);
  const [bridgePathsEffective, setBridgePathsEffective] = useState([]);
  // Compact UI preference shared with Chat (persisted)
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem('chronicle_compact') === '1'; } catch { return false; }
  });

  useEffect(() => {
    fetchDashboard();
    fetchPartners();
    fetchWebSearchStatus();
    fetchUsageStats();
    fetchCreditWarningSettings();
    fetchFeaturePricing();
    const token = readStoredToken();
    if (token) {
      axios
        .get(`${API_URL}/api/user/is-admin`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setIsOwner(!!res.data.is_admin))
        .catch(() => setIsOwner(false));
    }
  }, []);

  // Bridge reachability for Admin Overview
  useEffect(() => {
    if (activeAdminSection !== ADMIN_SECTIONS.OVERVIEW || !isOwner) return;
    const fetchBridge = async () => {
      try {
        const token = readStoredToken();
        if (!token) { setBridgeHostStatus(null); return; }
        const res = await axios.get(`${API_URL}/api/bridge/host-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBridgeHostStatus(res.data || null);
      } catch (err) {
        setBridgeHostStatus({ ok: false, url: '', error: err?.response?.data?.detail || 'unreachable' });
      }
    };
    fetchBridge();
    refreshSafeEditGuards();
  }, [activeAdminSection, isOwner]);

  const refreshSafeEditGuards = async () => {
    try {
      const token = readStoredToken();
      if (!token) { setSafeEditGuards(null); return; }
      const res = await axios.get(`${API_URL}/api/bridge/host-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSafeEditGuards(res.data?.ok ? { active: true } : { active: false });
    } catch { setSafeEditGuards({ active: false }); }
  };

  const refreshBridgeHostStatus = async () => {
    try {
      const token = readStoredToken();
      if (!token) { setBridgeHostStatus(null); return; }
      const res = await axios.get(`${API_URL}/api/bridge/host-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBridgeHostStatus(res.data || null);
    } catch (err) {
      setBridgeHostStatus({ ok: false, url: '', error: err?.response?.data?.detail || 'unreachable' });
    }
  };

  // Persist compact preference
  useEffect(() => {
    try { localStorage.setItem('chronicle_compact', compact ? '1' : '0'); } catch {}
  }, [compact]);

  // React to external compact changes (cross-view sync)
  useEffect(() => {
    const onCompactChange = (e) => {
      try {
        const v = String((e && e.detail) ?? localStorage.getItem('chronicle_compact')) === '1';
        setCompact(v);
      } catch {}
    };
    window.addEventListener('chronicle:compact-change', onCompactChange);
    return () => window.removeEventListener('chronicle:compact-change', onCompactChange);
  }, []);

  useEffect(() => {
    if (activeAdminSection !== 'secrets' || !isOwner) return;
    const load = async () => {
      setSecretsLoading(true);
      try {
        const token = readStoredToken();
        const res = await axios.get(`${API_URL}/api/admin/secrets-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSecretsStatus(res.data);
      } catch (err) {
        toast.error('Could not load secrets status');
        setSecretsStatus(null);
      } finally {
        setSecretsLoading(false);
      }
    };
    load();
  }, [activeAdminSection, isOwner]);

  useEffect(() => {
    if (activeAdminSection !== ADMIN_SECTIONS.BRIDGE_PATHS || !isOwner) return;
    const load = async () => {
      try {
        const token = readStoredToken();
        const res = await axios.get(`${API_URL}/api/admin/settings/bridge-paths`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBridgePathsEnv(res.data.env_prefixes || []);
        setBridgePathsEffective(res.data.effective_prefixes || []);
        const add = res.data.additional_prefixes || [];
        setBridgePathsLines(add.join('\n'));
      } catch (err) {
        toast.error('Could not load Bridge paths');
      }
    };
    load();
  }, [activeAdminSection, isOwner]);

  const saveBridgePaths = async () => {
    try {
      const token = readStoredToken();
      const lines = bridgePathsLines.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      await axios.post(
        `${API_URL}/api/admin/settings/bridge-paths`,
        { additional_prefixes: lines },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bridge paths saved');
      const res = await axios.get(`${API_URL}/api/admin/settings/bridge-paths`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBridgePathsEffective(res.data.effective_prefixes || []);
      setBridgePathsEnv(res.data.env_prefixes || []);
      setBridgePathsLines((res.data.additional_prefixes || []).join('\n'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Bridge paths');
    }
  };

  const fetchFeaturePricing = async () => {
    try {
      const token = readStoredToken();
      const res = await axios.get(`${API_URL}/api/admin/settings/feature-pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFeaturePricing(prev => ({ ...prev, ...(res.data || {}) }));
    } catch (err) {
      console.error('Failed to load feature pricing');
    }
  };

  const saveFeaturePricing = async () => {
    try {
      const token = readStoredToken();
      await axios.post(
        `${API_URL}/api/admin/settings/feature-pricing`,
        Object.fromEntries(
          Object.entries(featurePricing).map(([k, v]) => [k, Number(v)])
        ),
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Feature pricing saved');
    } catch (err) {
      toast.error('Failed to save feature pricing');
    }
  };

  const fetchCreditWarningSettings = async () => {
    try {
      const token = readStoredToken();
      const res = await axios.get(`${API_URL}/api/admin/settings/credit-warning`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCreditWarningThreshold(res.data.threshold ?? 20);
      setCreditWarningMessage(res.data.message || 'Your credits are running low. Please top up to continue.');
    } catch (err) {
      console.error('Failed to load credit warning settings');
    }
  };

  const saveCreditWarningSettings = async () => {
    try {
      const token = readStoredToken();
      await axios.post(
        `${API_URL}/api/admin/settings/credit-warning/config`,
        { threshold: Number(creditWarningThreshold), message: creditWarningMessage },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Credit warning settings saved');
    } catch (err) {
      toast.error('Failed to save credit warning settings');
    }
  };

  const fetchUsageStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/usage`);
      setUsageStats(res.data);
    } catch (err) {
      console.error('Failed to load usage stats');
    }
  };

  const fetchUserUsage = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/usage/user/${userId}`);
      setSelectedUserUsage(res.data);
    } catch (err) {
      console.error('Failed to load user usage');
    }
  };

  const fetchWebSearchStatus = async () => {
    try {
      const token = readStoredToken();
      const res = await axios.get(`${API_URL}/api/admin/settings/web-search`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWebSearchEnabled(res.data.enabled);
    } catch (err) {
      console.error('Failed to load web search status');
    }
  };

  const toggleWebSearch = async () => {
    try {
      const token = readStoredToken();
      await axios.post(`${API_URL}/api/admin/settings/web-search`, 
        { enabled: !webSearchEnabled },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      setWebSearchEnabled(!webSearchEnabled);
      toast.success(`Web search ${!webSearchEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update web search setting');
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard`);
      setStats(res.data);
      setUsers(res.data.recent_users || []);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users?search=${searchQuery}`);
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error('Search failed');
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/partners`);
      setPartners(res.data.partners || []);
    } catch (err) {
      console.error('Failed to load partners');
    }
  };

  const viewUser = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/user/${userId}`);
      setSelectedUser(res.data);
    } catch (err) {
      toast.error('Failed to load user');
    }
  };

  const updateCredits = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/user/${userId}/credits`, { credits: creditAmount });
      toast.success('Credits updated');
      fetchDashboard();
      setCreditAmount(0);
    } catch (err) {
      toast.error('Failed to update credits');
    }
  };

  const revokeAccess = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/revoke-access/${userId}`);
      toast.success('Access revoked');
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to revoke access');
    }
  };

  const restoreAccess = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/restore-access/${userId}`);
      toast.success('Access restored');
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to restore access');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/user/${userId}`);
      toast.success('User deleted');
      setSelectedUser(null);
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const setBridgeUiEligible = async (userId, enabled) => {
    try {
      const token = readStoredToken();
      await axios.post(
        `${API_URL}/api/admin/user/${userId}/bridge-ui-eligible`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(enabled ? 'PC Bridge UI enabled for this user' : 'PC Bridge UI disabled for this user');
      setSelectedUser((prev) => {
        if (!prev || prev.user?.id !== userId) return prev;
        return { ...prev, user: { ...prev.user, bridge_ui_eligible: enabled } };
      });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, bridge_ui_eligible: enabled } : u))
      );
      await viewUser(userId);
      fetchDashboard();
      if (searchQuery.trim()) {
        await searchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update PC Bridge UI');
    }
  };

  const addPartner = async () => {
    if (!newPartnerEmail) return;
    try {
      await axios.post(`${API_URL}/api/admin/partner`, { email: newPartnerEmail });
      toast.success('Partner added');
      setNewPartnerEmail('');
      fetchPartners();
    } catch (err) {
      toast.error('Failed to add partner');
    }
  };

  const removePartner = async (email) => {
    try {
      await axios.delete(`${API_URL}/api/admin/partner/${email}`);
      toast.success('Partner removed');
      fetchPartners();
    } catch (err) {
      toast.error('Failed to remove partner');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className={`admin-view ${compact ? 'compact' : ''}`} data-testid="admin-view">
      <div className="view-header">
        <h2><Shield size={24} /> Admin Panel</h2>
        <div className="view-header-actions">
          {isOwner && (
            <button
              type="button"
              className="mode-toggle-btn switchboard-nav-btn"
              title="Chronicle owner admin · Switchboard"
              onClick={() => openChronicleSwitchboard(true)}
              data-testid="admin-open-switchboard-btn"
            >
              Switchboard
            </button>
          )}
          <button
            onClick={() =>
              setCompact((c) => {
                const next = !c;
                try { localStorage.setItem('chronicle_compact', next ? '1' : '0'); } catch {}
                try { window.dispatchEvent(new CustomEvent('chronicle:compact-change', { detail: next ? '1' : '0' })); } catch {}
                return next;
              })
            }
            className={`mode-toggle-btn ${compact ? 'active' : ''}`}
            title={compact ? 'Compact mode ON' : 'Compact mode OFF'}
            data-testid="compact-toggle-admin"
          >
            Compact
          </button>
        </div>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <span className="stat-number">{stats.total_users}</span>
          <span className="stat-label">Total Users</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{stats.active_users}</span>
          <span className="stat-label">Active Users</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{stats.total_conversations}</span>
          <span className="stat-label">Conversations</span>
        </div>
      </div>

      <div className="admin-tab-nav">
        <button
          onClick={() =>
            setCompact((c) => {
              const next = !c;
              try { localStorage.setItem('chronicle_compact', next ? '1' : '0'); } catch {}
              try { window.dispatchEvent(new CustomEvent('chronicle:compact-change', { detail: next ? '1' : '0' })); } catch {}
              return next;
            })
          }
          className={`mode-toggle-btn ${compact ? 'active' : ''}`}
          title={compact ? 'Compact mode ON' : 'Compact mode OFF'}
          data-testid="compact-toggle-admin-inline"
        >
          Compact
        </button>
        <button
          className={activeAdminSection === ADMIN_SECTIONS.OVERVIEW ? 'active' : ''}
          onClick={() => setActiveAdminSection(ADMIN_SECTIONS.OVERVIEW)}
        >
          Overview
        </button>
        <button
          className={activeAdminSection === ADMIN_SECTIONS.USERS ? 'active' : ''}
          onClick={() => setActiveAdminSection(ADMIN_SECTIONS.USERS)}
        >
          Users
        </button>
        <button
          className={activeAdminSection === ADMIN_SECTIONS.PARTNERS ? 'active' : ''}
          onClick={() => setActiveAdminSection(ADMIN_SECTIONS.PARTNERS)}
        >
          Partners
        </button>
        <button
          className={activeAdminSection === ADMIN_SECTIONS.COMMS ? 'active' : ''}
          onClick={() => setActiveAdminSection(ADMIN_SECTIONS.COMMS)}
        >
          Comms
        </button>
        <button
          className={activeAdminSection === ADMIN_SECTIONS.USAGE ? 'active' : ''}
          onClick={() => setActiveAdminSection(ADMIN_SECTIONS.USAGE)}
        >
          Usage
        </button>
        {isOwner && (
        <button
          className={activeAdminSection === ADMIN_SECTIONS.SECRETS ? 'active' : ''}
          onClick={() => setActiveAdminSection(ADMIN_SECTIONS.SECRETS)}
        >
          <Key size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Secrets
        </button>
        )}
        {isOwner && (
        <button
          className={activeAdminSection === ADMIN_SECTIONS.BRIDGE_PATHS ? 'active' : ''}
          onClick={() => setActiveAdminSection(ADMIN_SECTIONS.BRIDGE_PATHS)}
        >
          <FolderOpen size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Bridge paths
        </button>
        )}
      </div>

      {activeAdminSection === ADMIN_SECTIONS.OVERVIEW && (
      <>
      <div className="admin-section">
        <h3>Feature Toggles</h3>
        <div className="feature-toggles">
          <div className="feature-toggle-item">
            <div className="feature-info">
              <Globe size={20} />
              <div>
                <span className="feature-name">Web Search</span>
                <span className="feature-desc">Allow users to search the internet</span>
              </div>
            </div>
            <button 
              onClick={toggleWebSearch}
              className={`toggle-btn ${webSearchEnabled ? 'active' : ''}`}
              data-testid="web-search-admin-toggle"
            >
              {webSearchEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-section">
        <h3>Bridge Status</h3>
        <p className="section-desc">Reachability of your Host Bridge from the server.</p>
        <div className="bridge-status-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>
            Status: {bridgeHostStatus ? (bridgeHostStatus.ok ? 'Up' : 'Down') : '—'}
          </span>
          {bridgeHostStatus?.url ? (
            <span className="muted">{bridgeHostStatus.url}</span>
          ) : null}
          {bridgeHostStatus?.error ? (
            <span className="muted">({bridgeHostStatus.error})</span>
          ) : null}
          <button type="button" onClick={refreshBridgeHostStatus}>Check now</button>
        </div>
      </div>

      <div className="admin-section">
        <h3>Safe Edit Guards Status</h3>
        <p className="section-desc">Protection against accidental file overwrites when Chronicle edits code.</p>
        <div className="bridge-status-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {safeEditGuards?.active ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              <strong>ACTIVE — Edits are protected</strong>
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
              <strong>INACTIVE — Bridge not reachable</strong>
            </span>
          )}
          <button type="button" onClick={refreshSafeEditGuards}>Check now</button>
        </div>
        {safeEditGuards?.active && (
          <div style={{ marginTop: 6, fontSize: 13, color: '#888' }}>
            <div>Auto-backup before writes: <strong>ON</strong></div>
            <div>500+ line file protection: <strong>ON</strong></div>
          </div>
        )}
      </div>

      <div className="admin-section">
        <h3>Credit Warning Settings</h3>
        <p className="section-desc">Users are warned at this threshold and blocked when credits hit zero.</p>
        <div className="credit-warning-controls">
          <input
            type="number"
            min="1"
            value={creditWarningThreshold}
            onChange={(e) => setCreditWarningThreshold(parseInt(e.target.value, 10) || 20)}
            placeholder="Warning threshold"
          />
          <textarea
            value={creditWarningMessage}
            onChange={(e) => setCreditWarningMessage(e.target.value)}
            placeholder="Low-credit warning message shown to users"
            rows={2}
          />
          <button onClick={saveCreditWarningSettings}>Save Warning Settings</button>
        </div>
      </div>
      </>
      )}

      {activeAdminSection === ADMIN_SECTIONS.USERS && (
      <>
      <div className="admin-section">
        <h3>Feature Pricing (Credits)</h3>
        <p className="section-desc">Change credit cost per feature. Set 0 to make a feature free.</p>
        <div className="credit-warning-controls">
          <input
            type="number"
            min="0"
            value={featurePricing.app_builder_fast}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, app_builder_fast: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Builder fast (per message)"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.app_builder_balanced}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, app_builder_balanced: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Builder balanced (per message)"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.app_builder_power}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, app_builder_power: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Builder power (per message)"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.voice_clone}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, voice_clone: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Voice clone"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.voice_tts}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, voice_tts: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Voice TTS"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.sms_send}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, sms_send: parseInt(e.target.value, 10) || 0 }))}
            placeholder="SMS send"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.call_make}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, call_make: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Call make"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.call_with_voice}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, call_with_voice: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Call with voice"
          />
          <input
            type="number"
            min="0"
            value={featurePricing.car_mode_conversation}
            onChange={(e) => setFeaturePricing(prev => ({ ...prev, car_mode_conversation: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Car mode conversation"
          />
          <button onClick={saveFeaturePricing}>Save Feature Pricing</button>
        </div>
      </div>

      <div className="admin-section">
        <h3>Search Users</h3>
        <div className="search-bar">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
          />
          <button onClick={searchUsers}>Search</button>
        </div>
      </div>

      <div className="admin-section">
        <h3>Users</h3>
        <div className="users-list">
          {users.map((u) => (
            <div key={u.id} className={`user-item ${u.access_revoked ? 'revoked' : ''}`} onClick={() => viewUser(u.id)}>
              <div className="user-info-admin">
                <span className="user-name">{u.name}</span>
                <span className="user-email">{u.email}</span>
              </div>
              <div className="user-credits">
                <span>{u.credits || 0} credits</span>
              </div>
              {u.access_revoked && <span className="revoked-badge">Revoked</span>}
              {u.bridge_ui_eligible && (
                <span className="revoked-badge" title="PC Bridge UI enabled" style={{ background: 'rgba(80,120,200,0.35)' }}>
                  Bridge UI
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedUser && (
        <div className="user-detail-modal">
          <div className="modal-content">
            <button className="close-modal" onClick={() => setSelectedUser(null)}>
              <X size={24} />
            </button>
            <h3>{selectedUser.user.name}</h3>
            <p>{selectedUser.user.email}</p>
            <div className="user-stats-detail">
              <span>Conversations: {selectedUser.stats.conversations}</span>
              <span>Contacts: {selectedUser.stats.contacts}</span>
              <span>Memories: {selectedUser.stats.memories}</span>
              <span>Credits: {selectedUser.user.credits || 0}</span>
            </div>
            <div className="credit-adjustment">
              <input
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                placeholder="Credits (+/-)"
              />
              <button onClick={() => updateCredits(selectedUser.user.id)}>Update Credits</button>
            </div>
            <div className="admin-bridge-access-box">
              <p className="admin-bridge-access-lead">
                Lets this account see <strong>Bridge OFF / ON</strong> on their <strong>Chat</strong> screen (they may need to refresh).
              </p>
              {!selectedUser.user.bridge_ui_eligible ? (
                <button
                  type="button"
                  className="admin-bridge-grant-btn"
                  onClick={() => setBridgeUiEligible(selectedUser.user.id, true)}
                >
                  <HardDrive size={20} aria-hidden />
                  Give this user access to the Bridge button
                </button>
              ) : (
                <>
                  <p className="admin-bridge-access-on">
                    Access on — they get the Bridge control in Chat.
                  </p>
                  <button
                    type="button"
                    className="admin-bridge-remove-btn"
                    onClick={() => setBridgeUiEligible(selectedUser.user.id, false)}
                  >
                    Remove Bridge access from this user
                  </button>
                </>
              )}
            </div>
            <div className="user-actions">
              {selectedUser.user.access_revoked ? (
                <button onClick={() => restoreAccess(selectedUser.user.id)} className="restore-btn">
                  <UserCheck size={20} /> Restore Access
                </button>
              ) : (
                <button onClick={() => revokeAccess(selectedUser.user.id)} className="revoke-btn">
                  <UserX size={20} /> Revoke Access
                </button>
              )}
              <button onClick={() => deleteUser(selectedUser.user.id)} className="delete-btn">
                <Trash2 size={20} /> Delete User
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {activeAdminSection === ADMIN_SECTIONS.PARTNERS && (
      <div className="admin-section">
        <h3>Admin Partners</h3>
        <div className="partners-list">
          {partners.map((p) => (
            <div key={p.email} className="partner-item">
              <span>{p.email}</span>
              <button onClick={() => removePartner(p.email)} className="remove-btn">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="add-partner">
          <input
            type="email"
            value={newPartnerEmail}
            onChange={(e) => setNewPartnerEmail(e.target.value)}
            placeholder="Partner email address"
          />
          <button onClick={addPartner}>Add Partner</button>
        </div>
      </div>
      )}

      {activeAdminSection === ADMIN_SECTIONS.COMMS && (
      <>
      <div className="admin-section">
        <h3>Phone & SMS</h3>
        <p className="section-desc">Send SMS via Twilio, place AI calls via Twilio, or open your phone dialer for a normal call</p>
        <div className="phone-controls">
          <input
            type="text"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Phone number (+44...)"
          />
          <input
            type="text"
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value)}
            placeholder="Message (for SMS)"
          />
          <div className="phone-buttons">
            <button onClick={async () => {
              if (!phoneNumber) { toast.error('Enter phone number'); return; }
              if (!smsMessage) { toast.error('Enter message'); return; }
              try {
                const res = await axios.post(`${API_URL}/api/sms/send`, { to: phoneNumber, message: smsMessage, use_native: false });
                toast.success('SMS sent via Twilio');
              } catch (err) { toast.error('Failed to send SMS'); }
            }} className="twilio-btn">
              SMS via Twilio
            </button>
            <button onClick={async () => {
              if (!phoneNumber) { toast.error('Enter phone number'); return; }
              try {
                const res = await axios.post(`${API_URL}/api/call/place`, { to: phoneNumber, use_native: false });
                toast.success('Call placed via Twilio');
              } catch (err) { toast.error('Failed to place call'); }
            }} className="twilio-btn">
              Call via Twilio
            </button>
            <button onClick={async () => {
              if (!phoneNumber) { toast.error('Enter phone number'); return; }
              try {
                const res = await axios.post(`${API_URL}/api/call/place`, { to: phoneNumber, use_native: true });
                if (res.data.native && res.data.link) {
                  window.location.href = res.data.link;
                } else {
                  toast.error('Could not open phone dialer');
                }
              } catch (err) {
                toast.error('Failed to open phone dialer');
              }
            }} className="native-btn">
              Call via My Phone (No AI / No Credits)
            </button>
          </div>
        </div>
      </div>
      <ElevenLabsCallSection phoneNumber={phoneNumber} />
      </>
      )}

      {activeAdminSection === ADMIN_SECTIONS.USAGE && (
      <>
      {/* API Usage Tracking Section */}
      <div className="admin-section">
        <h3>API Usage & Costs</h3>
        {usageStats ? (
          <div className="usage-tracking">
            <div className="usage-summary">
              <div className="usage-stat-card">
                <span className="usage-stat-value">${(usageStats.total_cost_cents / 100).toFixed(2)}</span>
                <span className="usage-stat-label">Total Cost</span>
              </div>
              <div className="usage-stat-card">
                <span className="usage-stat-value">{usageStats.total_calls}</span>
                <span className="usage-stat-label">Total API Calls</span>
              </div>
              <div className="usage-stat-card">
                <span className="usage-stat-value">{usageStats.calls_last_24h}</span>
                <span className="usage-stat-label">Last 24h</span>
              </div>
            </div>
            
            <div className="usage-by-api">
              <h4>By API</h4>
              <div className="api-usage-grid">
                {Object.entries(usageStats.by_api || {}).map(([api, data]) => (
                  <div key={api} className="api-usage-item">
                    <span className="api-name">{api}</span>
                    <span className="api-calls">{data.calls} calls</span>
                    <span className="api-cost">${(data.cost_cents / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="top-users-usage">
              <h4>Top Users by Cost</h4>
              <div className="top-users-list">
                {(usageStats.top_users || []).map((u, i) => (
                  <div key={u._id} className="top-user-item" onClick={() => fetchUserUsage(u._id)}>
                    <span className="top-user-rank">#{i + 1}</span>
                    <span className="top-user-email">{u.email || u.name || 'Unknown'}</span>
                    <span className="top-user-cost">${(u.total_cost_cents / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            {selectedUserUsage && (
              <div className="user-usage-detail">
                <h4>User Usage Detail</h4>
                <button className="close-usage-detail" onClick={() => setSelectedUserUsage(null)}>
                  <X size={16} />
                </button>
                <div className="user-usage-by-api">
                  {Object.entries(selectedUserUsage.by_api || {}).map(([api, data]) => (
                    <div key={api} className="user-api-item">
                      <span>{api}: {data.calls} calls, ${(data.cost_cents / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="user-recent-logs">
                  <h5>Recent Activity</h5>
                  {(selectedUserUsage.recent_logs || []).slice(0, 5).map((log, i) => (
                    <div key={i} className="log-item">
                      <span className="log-api">{log.api_name}</span>
                      <span className="log-details">{log.details}</span>
                      <span className="log-cost">${(log.cost_cents / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p>Loading usage data...</p>
        )}
      </div>
      </>
      )}

      {activeAdminSection === ADMIN_SECTIONS.SECRETS && isOwner && (
      <div className="admin-section">
        <h3><Key size={20} style={{ verticalAlign: 'middle' }} /> Broker &amp; keys</h3>
        <p className="section-desc">
          Owner only. Shows whether Broker Beast is reachable and which configured key names are present in the server process (values are never shown).
          Set on the host: <code>CHRONICLE_USE_BROKER</code>, <code>CHRONICLE_BROKER_TOKEN</code>, <code>CHRONICLE_BROKER_KEY_NAMES</code>.
        </p>
        {secretsLoading && <p>Loading…</p>}
        {!secretsLoading && secretsStatus && (
          <>
            <p><strong>Broker hydrate at startup:</strong> {secretsStatus.broker_enabled ? 'on' : 'off'}</p>
            <p><strong>Broker base URL:</strong> {secretsStatus.broker_base_url}</p>
            <p><strong>Client token set:</strong> {secretsStatus.broker_token_configured ? 'yes' : 'no'}</p>
            <p>
              <strong>Broker /health:</strong>{' '}
              {secretsStatus.broker_health_ok === true ? 'OK' : secretsStatus.broker_health_ok === false ? 'unreachable' : '—'}
              {secretsStatus.broker_error ? <span className="muted"> ({secretsStatus.broker_error})</span> : null}
            </p>
            <p>
              <strong>Broker API auth:</strong>{' '}
              {secretsStatus.broker_auth_ok === true ? 'OK' : secretsStatus.broker_auth_ok === false ? 'failed' : '—'}
            </p>
            <h4>Configured key names</h4>
            {!secretsStatus.key_names_configured?.length && (
              <p className="section-desc">No key list on server yet — add comma-separated names in <code>CHRONICLE_BROKER_KEY_NAMES</code>.</p>
            )}
            {(secretsStatus.keys || []).length > 0 && (
              <table className="admin-users-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr><th>Name</th><th>Present in env</th></tr>
                </thead>
                <tbody>
                  {(secretsStatus.keys || []).map((row) => (
                    <tr key={row.name}>
                      <td><code>{row.name}</code></td>
                      <td>{row.present_in_env ? 'yes' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
      )}

      {activeAdminSection === ADMIN_SECTIONS.BRIDGE_PATHS && isOwner && (
      <div className="admin-section">
        <h3><FolderOpen size={20} style={{ verticalAlign: 'middle' }} /> Chronicle Bridge — allowed folders</h3>
        <p className="section-desc">
          Owner only. Extra directory roots merged with env <code>CHRONICLE_BRIDGE_ALLOWED_PREFIXES</code> /{' '}
          <code>CHRONICLE_USER_WORKSPACE_PATH</code>. Chronicle uses these for host file tools; Chronicle Bridge (Node)
          reads the same list via <code>.chronicle_bridge_extra_prefixes.json</code> next to the backend (written when you save).
          One path per line; everything under each root is allowed.
        </p>
        <p className="section-desc"><strong>From env (read-only here):</strong></p>
        <ul style={{ marginTop: 4 }}>
          {(bridgePathsEnv || []).length === 0 && <li className="muted">(none from env)</li>}
          {(bridgePathsEnv || []).map((p) => (
            <li key={p}><code>{p}</code></li>
          ))}
        </ul>
        <p className="section-desc" style={{ marginTop: 12 }}><strong>Additional roots (saved to DB + shadow file):</strong></p>
        <textarea
          value={bridgePathsLines}
          onChange={(e) => setBridgePathsLines(e.target.value)}
          placeholder="Full path to a folder, one per line (Windows or server)"
          rows={6}
          style={{ width: '100%', fontFamily: 'monospace' }}
        />
        <button type="button" onClick={saveBridgePaths} style={{ marginTop: 8 }}>
          Save additional paths
        </button>
        <p className="section-desc" style={{ marginTop: 16 }}><strong>Effective roots (env ∪ additional):</strong></p>
        <ul style={{ marginTop: 4 }}>
          {(bridgePathsEffective || []).map((p) => (
            <li key={p}><code>{p}</code></li>
          ))}
        </ul>
      </div>
      )}
    </div>
  );
};

// ElevenLabs Call Section Component  
const ElevenLabsCallSection = ({ phoneNumber }) => {
  const [callMessage, setCallMessage] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [voices, setVoices] = useState([]);
  const [calling, setCalling] = useState(false);

  useEffect(() => {
    fetchVoices();
  }, []);

  const fetchVoices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/voices/available`);
      setVoices(res.data.voices || []);
    } catch (err) {
      console.error('Failed to load voices');
    }
  };

  const makeCallWithVoice = async () => {
    if (!phoneNumber) {
      toast.error('Enter phone number above');
      return;
    }
    if (!callMessage.trim()) {
      toast.error('Enter message to speak');
      return;
    }

    setCalling(true);
    try {
      const res = await axios.post(`${API_URL}/api/call/with-voice`, {
        to_phone: phoneNumber,
        message: callMessage,
        voice_id: selectedVoice || null
      });
      toast.success('Call initiated with ElevenLabs voice!');
      setCallMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to make call');
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className="admin-section call-voice-section">
      <h3><Volume2 size={20} /> Call with AI Voice</h3>
      <p className="section-desc">Make a call using ElevenLabs voices (your clone or presets)</p>
      <div className="call-voice-form">
        <textarea
          value={callMessage}
          onChange={(e) => setCallMessage(e.target.value)}
          placeholder="What should Chronicle say on the call?"
          data-testid="call-message-input"
        />
        <div className="voice-select-row">
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            data-testid="voice-select"
          >
            <option value="">Default Voice</option>
            {voices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.name} {voice.is_clone ? '(Your Clone)' : ''}
              </option>
            ))}
          </select>
          <button
            onClick={makeCallWithVoice}
            disabled={calling}
            className="call-elevenlabs-btn"
            data-testid="call-with-voice-btn"
          >
            {calling ? (
              <>Calling...</>
            ) : (
              <>
                <Phone size={18} />
                Call with Voice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard
const Dashboard = () => {
  const [activeView, setActiveView] = useState('chat');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(CHRONICLE_SIDEBAR_COLLAPSED_KEY);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch {}
    return true;
  });
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showAdminNav, setShowAdminNav] = useState(false);
  /** Session id for PC Bridge when Chat isn't mounted (e.g. ?admin=1). Synced from ChatView when chat is open. */
  const [sidebarBridgeSessionId, setSidebarBridgeSessionId] = useState(null);
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /** Open admin panel when URL had `admin=1` or we stored intent before query was stripped. */
  useEffect(() => {
    const rawSearch =
      (typeof window !== 'undefined' && window.location.search) ||
      location.search ||
      '';
    let stored = false;
    try {
      stored = sessionStorage.getItem(CHRONICLE_DEEP_LINK_ADMIN_KEY) === '1';
    } catch {
      stored = false;
    }
    const params = new URLSearchParams(rawSearch || '');
    const wantAdmin = params.get('admin') === '1' || stored;
    if (wantAdmin && showAdminNav) {
      setActiveView('admin');
      try {
        sessionStorage.removeItem(CHRONICLE_DEEP_LINK_ADMIN_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [showAdminNav, location.pathname, location.search, location.key]);

  useEffect(() => {
    let cancelled = false;
    const loadAdminNav = async () => {
      const t = readStoredToken();
      if (!t) {
        if (!cancelled) setShowAdminNav(false);
        try {
          sessionStorage.removeItem(CHRONICLE_DEEP_LINK_ADMIN_KEY);
        } catch {
          /* ignore */
        }
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/user/is-admin`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (!cancelled) {
          const ok = !!(res.data?.is_admin || res.data?.is_partner);
          setShowAdminNav(ok);
          if (!ok) {
            try {
              sessionStorage.removeItem(CHRONICLE_DEEP_LINK_ADMIN_KEY);
            } catch {
              /* ignore */
            }
          }
        }
      } catch {
        if (!cancelled) {
          setShowAdminNav(false);
          try {
            sessionStorage.removeItem(CHRONICLE_DEEP_LINK_ADMIN_KEY);
          } catch {
            /* ignore */
          }
        }
      }
    };
    loadAdminNav();
    return () => {
      cancelled = true;
    };
  }, [user?.id, token]);

  useEffect(() => {
    /** Sidebar Bridge needs a real session id when not on local billing bypass, or when Admin is shown without Chat (bootstrap early). */
    if (!SKIP_BILLING_GATE && !showAdminNav) return;
    let cancelled = false;
    const t = readStoredToken();
    if (!t) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/chat/sessions`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (cancelled) return;
        const sessions = res.data?.sessions || [];
        setSidebarBridgeSessionId((prev) => {
          if (prev) return prev;
          if (sessions.length > 0) return sessions[0]._id;
          return typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        });
      } catch {
        if (!cancelled) {
          setSidebarBridgeSessionId((prev) => {
            if (prev) return prev;
            return typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, token, showAdminNav]);

  // Check if mobile
  const isMobile = () => window.innerWidth <= 768;

  // Toggle sidebar - different behavior for mobile vs desktop
  const toggleSidebar = () => {
    if (isMobile()) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed((c) => {
        const next = !c;
        try { localStorage.setItem(CHRONICLE_SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch {}
        return next;
      });
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Listen for service worker updates (production only — dev + SW update() throws the red error overlay)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) return;
    const onMsg = (event) => {
      if (event.data?.type === 'UPDATE_AVAILABLE') {
        setUpdateAvailable(true);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    const checkForUpdates = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
        }
      } catch (e) {
        /* ignore */
      }
    };
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    return () => {
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener('message', onMsg);
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload(true);
  };

  const handleLogout = () => {
    logout();
    navigate(SKIP_BILLING_GATE ? '/' : '/auth');
  };

  // Calculate days remaining in trial
  const getTrialDaysRemaining = () => {
    if (!user?.trial_ends_at) return 0;
    const trialEnd = new Date(user.trial_ends_at);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const trialDaysRemaining = getTrialDaysRemaining();
  const showTrialBanner = user?.trial_active && !user?.is_subscribed;
  const showTrialExpiredBanner = !user?.trial_active && !user?.is_subscribed;

  const renderView = () => {
    switch (activeView) {
      case 'chat':
        return (
          <ChatView
            onBridgeSessionIdChange={setSidebarBridgeSessionId}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={toggleSidebar}
          />
        );
      case 'car':
        return <CarModeView onExit={() => setActiveView('chat')} />;
      case 'contacts':
        return <ContactsView />;
      case 'memory':
        return <MemoryView />;
      case 'phone':
        return <PhoneView />;
      case 'subscription':
        return <SubscriptionView />;
      case 'settings':
        return <UserSettingsView />;
      case 'admin':
        return <OwnerAdminView />;
      default:
        return (
          <ChatView
            onBridgeSessionIdChange={setSidebarBridgeSessionId}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={toggleSidebar}
          />
        );
    }
  };

  return (
    <div className="dashboard">
      {SKIP_BILLING_GATE && (
        <button
          className="compact-float-btn"
          title="Compact"
          onClick={() => {
            try {
              const now = localStorage.getItem('chronicle_compact') === '1';
              const next = now ? '0' : '1';
              localStorage.setItem('chronicle_compact', next);
              window.dispatchEvent(new CustomEvent('chronicle:compact-change', { detail: next }));
            } catch {}
          }}
          data-testid="compact-toggle-fallback"
        >
          Compact
        </button>
      )}
      {updateAvailable && (
        <div className="update-banner" data-testid="update-banner">
          <span>An update is available!</span>
          <button onClick={handleRefresh} className="refresh-btn">Refresh Now</button>
        </div>
      )}
      <button 
        className="mobile-menu-btn"
        onClick={toggleSidebar}
        data-testid="mobile-menu-btn"
      >
        <Menu size={24} />
      </button>

      {activeView === 'chat' && sidebarCollapsed && !sidebarOpen && window.innerWidth > 768 ? (
        <button
          type="button"
          className="sidebar-rail-toggle"
          onClick={toggleSidebar}
          aria-label="Open menu"
          data-testid="sidebar-rail-toggle"
        >
          <Menu size={22} />
        </button>
      ) : null}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <button 
          className="sidebar-close-btn"
          onClick={toggleSidebar}
          aria-label="Toggle menu"
        >
          <ChevronLeft size={24} />
        </button>
        
        <div className="sidebar-header">
          <Brain size={32} />
          <h1>Chronicle</h1>
        </div>

        <nav className="sidebar-nav">
          <button
            className={activeView === 'chat' ? 'active' : ''}
            onTouchEnd={(e) => { e.preventDefault(); setActiveView('chat'); setSidebarOpen(false); }}
            onClick={() => { setActiveView('chat'); setSidebarOpen(false); }}
            data-testid="nav-chat"
          >
            <MessageSquare size={20} />
            Chat
          </button>
          <button
            className={activeView === 'car' ? 'active' : ''}
            onTouchEnd={(e) => { e.preventDefault(); setActiveView('car'); setSidebarOpen(false); }}
            onClick={() => { setActiveView('car'); setSidebarOpen(false); }}
            data-testid="nav-car"
          >
            <Car size={18} />
            Car Mode
          </button>
          <button
            className={activeView === 'phone' ? 'active' : ''}
            onTouchEnd={(e) => { e.preventDefault(); setActiveView('phone'); setSidebarOpen(false); }}
            onClick={() => { setActiveView('phone'); setSidebarOpen(false); }}
            data-testid="nav-phone"
          >
            <Phone size={20} />
            Phone & SMS
          </button>
          <button
            className={activeView === 'contacts' ? 'active' : ''}
            onTouchEnd={(e) => { e.preventDefault(); setActiveView('contacts'); setSidebarOpen(false); }}
            onClick={() => { setActiveView('contacts'); setSidebarOpen(false); }}
            data-testid="nav-contacts"
          >
            <Users size={20} />
            Contacts
          </button>
          <button
            className={activeView === 'memory' ? 'active' : ''}
            onTouchEnd={(e) => { e.preventDefault(); setActiveView('memory'); setSidebarOpen(false); }}
            onClick={() => { setActiveView('memory'); setSidebarOpen(false); }}
            data-testid="nav-memory"
          >
            <Brain size={20} />
            Memory
          </button>
          <button
            className={activeView === 'subscription' ? 'active' : ''}
            onTouchEnd={(e) => { e.preventDefault(); setActiveView('subscription'); setSidebarOpen(false); }}
            onClick={() => { setActiveView('subscription'); setSidebarOpen(false); }}
            data-testid="nav-subscription"
          >
            <CreditCard size={20} />
            Subscription
          </button>
          <button
            className={activeView === 'settings' ? 'active' : ''}
            onTouchEnd={(e) => { e.preventDefault(); setActiveView('settings'); setSidebarOpen(false); }}
            onClick={() => { setActiveView('settings'); setSidebarOpen(false); }}
            data-testid="nav-settings"
          >
            <Settings size={20} />
            Settings
          </button>
          {token && (
            <button
              type="button"
              onTouchEnd={(e) => {
                e.preventDefault();
                openChronicleSwitchboard(!!showAdminNav);
                setSidebarOpen(false);
              }}
              onClick={() => {
                openChronicleSwitchboard(!!showAdminNav);
                setSidebarOpen(false);
              }}
              data-testid="nav-switchboard"
            >
              <Globe size={20} />
              Switchboard
            </button>
          )}
          {showAdminNav && (
            <button
              className={activeView === 'admin' ? 'active' : ''}
              onTouchEnd={(e) => { e.preventDefault(); setActiveView('admin'); setSidebarOpen(false); }}
              onClick={() => { setActiveView('admin'); setSidebarOpen(false); }}
              data-testid="nav-admin"
            >
              <Shield size={20} />
              Admin
            </button>
          )}
        </nav>

        <BridgeUnlockControls
          sessionId={sidebarBridgeSessionId}
          wrapperClassName="sidebar-bridge-unlock"
        />

        <div className="sidebar-footer">
          <div className="user-info">
            <User size={20} />
            <span>{user?.name}</span>
          </div>
          <button onClick={handleLogout} className="logout-btn" data-testid="logout-btn">
            <LogOut size={20} />
          </button>
        </div>
        <div className="app-footer">
          Chronicle - The AI that never forgets © 2026 Useful Gadgets Ltd
        </div>
      </aside>

      {/* Overlay to close sidebar when clicking outside */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <main
        className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${
          activeView === 'admin' || activeView === 'settings' ? 'main-content-scroll' : ''
        }`}
      >
        {showTrialExpiredBanner && (
          <div className="trial-banner trial-expired" data-testid="trial-expired-banner">
            <div className="trial-banner-content">
              <span>Your free trial has ended. Subscribe to continue using Chronicle - your memories are preserved!</span>
              <button 
                onClick={() => setActiveView('subscription')}
                className="trial-subscribe-btn"
                data-testid="trial-subscribe-btn"
              >
                Subscribe Now
              </button>
            </div>
          </div>
        )}
        {showTrialBanner && (
          <div className="trial-banner trial-active" data-testid="trial-active-banner">
            <div className="trial-banner-content">
              <span>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} left in your free trial</span>
              <button 
                onClick={() => setActiveView('subscription')}
                className="trial-upgrade-btn"
                data-testid="trial-upgrade-btn"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        )}
        {renderView()}
      </main>
    </div>
  );
};

/** Local dev: block /auth bounce — retry dev-login until the API responds. */
function DevLocalSessionGate() {
  const { tryDevLogin } = useAuth();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer;
    (async () => {
      const ok = await tryDevLogin();
      if (!cancelled && !ok) timer = setTimeout(() => setTick((n) => n + 1), 2000);
    })();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tryDevLogin, tick]);
  return (
    <div className="loading-screen">
      <Brain size={48} className="spin" />
      <p>Connecting…</p>
    </div>
  );
}

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <Brain size={48} className="spin" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    if (SKIP_BILLING_GATE && !NORMAL_LOGIN_ONLY) return <DevLocalSessionGate />;
    return <Navigate to="/auth" replace />;
  }

  // Local / compose dev: skip paywall & disclosure so Stripe keys are not required.
  if (!SKIP_BILLING_GATE) {
    if (!user.disclosure_accepted) {
      return <Navigate to="/disclosure" replace />;
    }
    if (!user.card_on_file && !user.is_subscribed) {
      return <Navigate to="/trial-setup" replace />;
    }
  }

  return children;
};

// Admin Page Component
const AdminPage = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const ADMIN_EMAIL = 'sailingwaves@gmail.com';

  useEffect(() => {
    if (user?.email !== ADMIN_EMAIL) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchLinks();
  }, [user, navigate]);

  const fetchLinks = async () => {
    try {
      const token = readStoredToken();
      const response = await axios.get(`${API}/api/admin/magic-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks(response.data.links || []);
    } catch (err) {
      toast.error('Failed to load magic links');
    } finally {
      setLoading(false);
    }
  };

  const createLink = async () => {
    setCreating(true);
    try {
      const token = readStoredToken();
      const response = await axios.post(`${API}/api/admin/magic-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Magic link created');
      navigator.clipboard.writeText(response.data.url);
      toast.success('Link copied to clipboard');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const revokeAccess = async (userId) => {
    try {
      const token = readStoredToken();
      await axios.post(`${API}/api/admin/revoke-access/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Access revoked');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to revoke access');
    }
  };

  const restoreAccess = async (userId) => {
    try {
      const token = readStoredToken();
      await axios.post(`${API}/api/admin/restore-access/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Access restored');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to restore access');
    }
  };

  if (user?.email !== ADMIN_EMAIL) {
    return null;
  }

  return (
    <div className="admin-page" data-testid="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>Admin Panel</h1>
          <button onClick={() => navigate('/')} className="back-btn">Back to App</button>
        </div>

        <div className="admin-section">
          <h2>Magic Links</h2>
          <p>Create one-time invite links for free accounts</p>
          
          <button 
            onClick={createLink} 
            disabled={creating}
            className="create-link-btn"
            data-testid="create-magic-link-btn"
          >
            <Link size={18} />
            {creating ? 'Creating...' : 'Create Magic Link'}
          </button>
        </div>

        <div className="admin-section">
          <h2>Invited Users</h2>
          {loading ? (
            <p>Loading...</p>
          ) : links.length === 0 ? (
            <p>No magic links created yet</p>
          ) : (
            <div className="links-list">
              {links.map(link => (
                <div key={link.id} className="link-item">
                  <div className="link-info">
                    <span className={`link-status ${link.used ? 'used' : link.revoked ? 'revoked' : 'available'}`}>
                      {link.used ? 'Used' : link.revoked ? 'Revoked' : 'Available'}
                    </span>
                    {link.user_info && (
                      <span className="user-email">{link.user_info.email}</span>
                    )}
                    <span className="link-date">{new Date(link.created_at).toLocaleDateString()}</span>
                  </div>
                  {link.used && link.used_by && (
                    <button
                      onClick={() => link.access_revoked ? restoreAccess(link.used_by) : revokeAccess(link.used_by)}
                      className={link.access_revoked ? 'restore-btn' : 'revoke-btn'}
                    >
                      {link.access_revoked ? <UserCheck size={16} /> : <UserX size={16} />}
                      {link.access_revoked ? 'Restore' : 'Revoke'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// App Component
function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster position="bottom-right" richColors closeButton />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

// Privacy Policy Page
const PrivacyPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: April 2026</p>
        
        <section>
          <h2>1. Information We Collect</h2>
          <p>Chronicle collects information you provide directly, including:</p>
          <ul>
            <li>Account information (name, email address)</li>
            <li>Messages and conversations with our AI assistant</li>
            <li>Contacts you add to the app</li>
            <li>Voice recordings for transcription</li>
            <li>Payment information (processed securely via Stripe)</li>
          </ul>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and improve our AI assistant services</li>
            <li>Maintain conversation memory across sessions</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send SMS and make calls on your behalf (when requested)</li>
            <li>Communicate with you about your account</li>
          </ul>
        </section>

        <section>
          <h2>3. Data Storage and Security</h2>
          <p>Your data is stored securely and encrypted. We use industry-standard security measures to protect your information. Conversation data and memories are stored to provide continuity in your AI assistant experience.</p>
        </section>

        <section>
          <h2>4. Third-Party Services</h2>
          <p>We use the following third-party services:</p>
          <ul>
            <li>Google OAuth for authentication</li>
            <li>Stripe for payment processing</li>
            <li>Twilio for SMS and voice calls</li>
            <li>ElevenLabs for voice synthesis</li>
            <li>OpenAI for voice transcription</li>
            <li>Anthropic for AI conversations</li>
          </ul>
        </section>

        <section>
          <h2>5. Your Rights</h2>
          <p>You can request to delete your account and all associated data at any time through the Settings page or by contacting us.</p>
        </section>

        <section>
          <h2>6. Contact Us</h2>
          <p>For privacy-related questions, contact us at: ceo@usefulgadgets.ltd</p>
        </section>

        <a href="/" className="back-link">← Back to Chronicle</a>
      </div>
    </div>
  );
};

// Terms of Service Page
const TermsPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: April 2026</p>
        
        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>By accessing and using Chronicle, you agree to be bound by these Terms of Service. If you do not agree, please do not use the service.</p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>Chronicle is an AI-powered personal assistant that provides:</p>
          <ul>
            <li>Conversational AI with persistent memory</li>
            <li>SMS and voice call capabilities</li>
            <li>Contact management</li>
            <li>Voice transcription and synthesis</li>
          </ul>
        </section>

        <section>
          <h2>3. Account Registration</h2>
          <p>You must provide accurate information when creating an account. You are responsible for maintaining the security of your account credentials.</p>
        </section>

        <section>
          <h2>4. Subscription and Payment</h2>
          <p>Chronicle offers a 10-day free trial. After the trial, continued use requires a paid subscription. Payments are processed securely through Stripe. You may cancel your subscription at any time.</p>
        </section>

        <section>
          <h2>5. Acceptable Use</h2>
          <p>You agree not to use Chronicle for:</p>
          <ul>
            <li>Any illegal or unauthorized purpose</li>
            <li>Harassment, spam, or abuse</li>
            <li>Attempting to gain unauthorized access</li>
            <li>Interfering with the service's operation</li>
          </ul>
        </section>

        <section>
          <h2>6. Limitation of Liability</h2>
          <p>Chronicle is provided "as is" without warranties. We are not liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
        </section>

        <section>
          <h2>7. Changes to Terms</h2>
          <p>We may update these terms from time to time. Continued use of the service after changes constitutes acceptance of the new terms.</p>
        </section>

        <section>
          <h2>8. Contact</h2>
          <p>For questions about these terms, contact us at: ceo@usefulgadgets.ltd</p>
        </section>

        <a href="/" className="back-link">← Back to Chronicle</a>
      </div>
    </div>
  );
};

// Separate component to handle routing
function AppRoutes() {
  return (
    <Routes>
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      <Route path="/disclosure" element={<DisclosurePage />} />
      <Route path="/trial-setup" element={<TrialSetupPage />} />
      <Route path="/trial/success" element={<TrialSuccess />} />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route
        path="/subscription/hosting-success"
        element={
          <ProtectedRoute>
            <HostingSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
